import React, { useState, useEffect, useContext, useRef  } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faTimes,
  faCheck,
  faChevronDown,
  faSearch,
  faX,
  faArrowRight,
  faCalendarAlt,
  // Use these alternatives if the above icons don't exist
  faCog as faRules,           // or faListAlt, faClipboardList
  faCoins,                    // this should exist
  faCheckCircle,              // this should exist  
  faClock, 
  faSpinner,                    // this should exist
  faExclamationTriangle,
  faEye,
  faImage
} from '@fortawesome/free-solid-svg-icons';
import { 
  ArrowRight, 
  CheckCircle,
} from 'lucide-react';
import { Spinner } from '../../Loader/Loader';
import { decryptData } from '../../../utils/storageCrypto';

// Add after imports, before components
const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
};

// NEW: Global helper for backend date conversion with defaults (used in transformBackendDataToFrontend)
const convertBackendDateToDisplayWithDefault = (backendDate, isToDate = false) => {
  if (!backendDate || backendDate === null || backendDate === '') {
    const today = new Date();
    if (isToDate) {
      today.setFullYear(today.getFullYear() + 1);  // 1 year ahead for toDate
    }
    return formatDateForDisplay(today.toISOString());  // DD/MM/YYYY
  }
  // Valid date: convert ISO/full ISO to DD/MM/YYYY
  return formatDateForDisplay(backendDate);
};

// UPDATED: Enhanced global convertToISODate to handle empty/invalid with defaults
const convertToISODate = (inputDate, isToDate = false) => {  // Optional param to distinguish toDate
  if (!inputDate || inputDate === '' || inputDate === null) {
    const today = new Date();
    if (isToDate) {
      today.setFullYear(today.getFullYear() + 1);  // Default toDate: 1 year ahead
    }
    return today.toISOString();  // Full ISO: "2024-10-11T00:00:00.000Z"
  }


  // Handle DD/MM/YYYY format
  if (inputDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    try {
      const [day, month, year] = inputDate.split('/');
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
      const dateObj = new Date(isoDate);
      if (dateObj.getFullYear() == year && dateObj.getMonth() == (month - 1) && dateObj.getDate() == day) {
        return isoDate;
      }
      // Invalid: fallback to default
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    } catch (error) {
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    }
  }
   // Handle YYYY-MM-DD (from local DateInput)
  if (inputDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    try {
      const dateObj = new Date(inputDate + 'T00:00:00.000Z');
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toISOString();
      }
      // Fallback
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    } catch (error) {
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    }
  }
  // Handle full ISO
  if (inputDate.match(/^\d{4}-\d{2}-\d{2}T/)) {
    try {
      const dateObj = new Date(inputDate);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toISOString();
      }
      // Fallback
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    } catch (error) {
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      return today.toISOString();
    }
  }

    // Invalid: fallback
  const today = new Date();
  if (isToDate) today.setFullYear(today.getFullYear() + 1);
  return today.toISOString();
};


const RewardRuleContext = React.createContext();

// Context Provider Component
const RewardRuleProvider = ({ children }) => {
  const [sharedData, setSharedData] = useState({
    selectedCategory: null,
    selectedBrand: null,
    fetchedItems: []
  });

  return (
    <RewardRuleContext.Provider value={{ sharedData, setSharedData }}>
      {children}
    </RewardRuleContext.Provider>
  );
};

// Custom hook to use the context
const useRewardRuleContext = () => {
  const context = useContext(RewardRuleContext);
  if (!context) {
    throw new Error('useRewardRuleContext must be used within RewardRuleProvider');
  }
  return context;
};

// Minimalistic Card Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

// Modern Button Component
const Button = ({ 
  children, 
  variant = "primary", 
  size = "md", 
  className = "", 
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variants = {
    primary: "bg-gradient-to-r from-[#f7525a] to-[#e6474f] text-white hover:from-[#e6474f] hover:to-[#d63c43] focus:ring-[#f7525a]/20 shadow-sm hover:shadow-md",
    secondary: "bg-gray-50 text-gray-700 hover:bg-gray-100 focus:ring-gray-500/20 border border-gray-200",
    outline: "border border-gray-200 text-gray-700 hover:bg-gray-50 focus:ring-gray-500/20",
    destructive: "bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500/20 border border-red-200",
    ghost: "text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:ring-gray-500/20"
  };
  
  const sizes = {
    sm: "px-2 py-1 text-sm",
    md: "px-2 py-1 text-sm",
    lg: "px-2 py-1 text-sm"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Modern Input Component
const Input = ({ 
  className = "", 
  type = "text", 
  placeholder = "",
  value,
  onChange,
  disabled = false,
  icon,
  ...props 
}) => {
  return (
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <FontAwesomeIcon icon={icon} className="text-sm" />
        </div>
      )}
      <input
        type={type}
        className={`w-full px-2 py-1 text-sm bg-gray-50 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400 ${icon ? 'pl-10' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
    </div>
  );
};

// Update the Select component to pass options to SelectValue
const Select = ({ children, value, onValueChange, className = "", placeholder = "Select option", searchable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [searchTerm, setSearchTerm] = useState('');

  // Extract options from children for SelectValue
  const options = React.Children.toArray(children)
    .filter(child => child.type === SelectItem)
    .map(child => ({
      value: child.props.value,
      label: child.props.children,
      name: child.props.children
    }));

  // Update selectedValue when value prop changes
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleSelect = (newValue) => {
    setSelectedValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  // Filter children based on search term if searchable
  const filteredChildren = searchable && searchTerm ? 
    React.Children.toArray(children).filter(child => {
      if (child.type === SelectItem) {
        const childText = typeof child.props.children === 'string' 
          ? child.props.children.toLowerCase() 
          : '';
        return childText.includes(searchTerm.toLowerCase());
      }
      return true;
    }) : children;

  return (
    <div className={`relative ${className}`}>
      <SelectTrigger onClick={() => setIsOpen(!isOpen)} value={selectedValue}>
        <SelectValue placeholder={placeholder} value={selectedValue} options={options} />
      </SelectTrigger>
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg max-h-60 overflow-hidden">
          {searchable && (
            <div className="p-1 border-b border-gray-100">
              <div className="relative">
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  className="w-full pl-10 pr-10 p-1 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
            </div>
          )}
          <SelectContent>
            {React.Children.count(filteredChildren) > 0 ? (
              React.Children.map(filteredChildren, (child, index) => {
                if (child.type === SelectItem) {
                  return React.cloneElement(child, { 
                    key: child.key || index,
                    onSelect: handleSelect, 
                    onClose: handleClose,
                    isSelected: child.props.value === selectedValue
                  });
                }
                return child;
              })
            ) : (
              searchable && searchTerm ? (
                <div className="px-4 py-8 text-center">
                  <div className="text-gray-400 text-sm">
                    No options found for "{searchTerm}"
                  </div>
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <div className="text-gray-400 text-sm">
                    No options available
                  </div>
                </div>
              )
            )}
          </SelectContent>
        </div>
      )}
    </div>
  );
};

const SelectTrigger = ({ children, onClick, value, className = "" }) => (
  <button
    type="button"
    className={`w-full px-2 py-1 text-sm bg-gray-50 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 flex items-center justify-between hover:bg-gray-100 ${className}`}
    onClick={onClick}
  >
    {children}
    <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs" />
  </button>
);

// Update the SelectValue component to handle label display
const SelectValue = ({ placeholder, value, options = [], displayKey = null }) => {
  let displayValue = placeholder;
  
  if (value) {
    if (options && options.length > 0) {
      // Find the option that matches the value
      const selectedOption = options.find(option => {
        if (displayKey) {
          return option[displayKey] === value || option.value === value || option.id === value;
        }
        return option.value === value || option.label === value || option.name === value || option === value;
      });
      
      if (selectedOption) {
        // Display the label/name instead of value
        displayValue = selectedOption.label || selectedOption.name || selectedOption[displayKey] || selectedOption.value || selectedOption;
      } else {
        displayValue = value;
      }
    } else {
      displayValue = value;
    }
  }
  
  return (
    <span className={value ? "text-gray-900" : "text-gray-400"}>
      {displayValue}
    </span>
  );
};

const SelectContent = ({ children }) => (
  <div className="py-2 max-h-48 overflow-y-auto">
    {children}
  </div>
);

// COMPLETELY FIXED SelectItem component with proper error handling
const SelectItem = ({ children, value, onSelect, onClose, isSelected }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Safety check for onSelect function
    if (onSelect && typeof onSelect === 'function') {
      onSelect(value);
    } else {
      console.warn('SelectItem: onSelect is not a function or is undefined');
    }
    
    // Safety check for onClose function
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <button
      type="button"
      className={`w-full px-2 py-1 text-sm text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 ${
        isSelected ? 'bg-gray-100 text-[#f7525a]' : ''
      }`}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

// Modern Multi-Select Dropdown
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleOptionToggle = (optionId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    let newSelectedValues;
    
    if (optionId === "all") {
      if (selectedValues.includes("all") || isAllIndividualItemsSelected()) {
        newSelectedValues = [];
      } else {
        const individualOptions = options.filter(opt => opt.id !== "all");
        newSelectedValues = individualOptions.map(option => option.id);
      }
    } else {
      if (selectedValues.includes(optionId)) {
        newSelectedValues = selectedValues.filter(id => id !== optionId);
      } else {
        newSelectedValues = [...selectedValues, optionId];
      }
      
      newSelectedValues = newSelectedValues.filter(id => id !== "all");
    }
    
    onChange(newSelectedValues);
  };

  const isAllIndividualItemsSelected = () => {
    const individualOptions = options.filter(opt => opt.id !== "all");
    const selectedIndividualOptions = selectedValues.filter(id => id !== "all");
    return selectedIndividualOptions.length === individualOptions.length && individualOptions.length > 0;
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder || "Select items";
    
    if (isAllIndividualItemsSelected()) {
      return "All items selected";
    }
    
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.id === selectedValues[0]);
      return option ? option.name : "";
    }
    return `${selectedValues.length} items selected`;
  };

  return (
    <div className="relative flex-1">
      <button
        type="button"
        className="w-full h-7 px-2 py-1 text-sm bg-gray-50 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 flex items-center justify-between hover:bg-gray-100"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValues.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {getDisplayText()}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`text-gray-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg max-h-80 overflow-hidden">
          {/* Search Header */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                className="w-full pl-10 pr-10 py-2 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchTerm('');
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              )}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isChecked = option.id === "all" 
                  ? isAllIndividualItemsSelected() 
                  : selectedValues.includes(option.id);

                return (
                  <label
                    key={option.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleOptionToggle(option.id, e)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                        isChecked 
                          ? 'bg-[#f7525a] border-[#f7525a]' 
                          : 'border-gray-300 group-hover:border-gray-400'
                      }`}>
                        {isChecked && (
                          <FontAwesomeIcon 
                            icon={faCheck} 
                            className="text-white text-xs absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" 
                          />
                        )}
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-gray-900 select-none">
                      {option.name}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <div className="text-gray-400 text-sm">
                  {searchTerm ? `No items found for "${searchTerm}"` : 'No items available'}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 font-medium">
                {selectedValues.length} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange([]);
                  }}
                  className="text-xs px-3 py-1"
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className="text-xs px-3 py-1"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Themed Modal for Applicable Entities Details
const EntityDetailsModal = ({ 
  isOpen, 
  onClose, 
  entities = [], 
  type,  // 'reward' or 'redeem'
  onSave 
}) => {
  const [editedEntities, setEditedEntities] = useState(entities);

  useEffect(() => {
    setEditedEntities(entities);  // Reset on open
  }, [entities, isOpen]);

  const handleFieldChange = (entityIndex, field, value) => {
    setEditedEntities(prev => 
      prev.map((entity, i) => 
        i === entityIndex ? { ...entity, [field]: value } : entity
      )
    );
  };

  const handleSave = () => {
    onSave(editedEntities);
    onClose();
  };

  if (!isOpen) return null;

  const isReward = type === 'reward';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#f7525a] to-[#e6474f] text-white p-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold">
              <FontAwesomeIcon icon={faEye} className="mr-2" />
              {isReward ? 'Rewardable Entities Details' : 'Redeemable Entities Details'}
            </h3>
            <Button 
              variant="ghost" 
              onClick={onClose} 
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
          <p className="text-sm opacity-90 mt-1">
            Edit details for {entities.length} selected {isReward ? 'rewardable' : 'redeemable'} entities.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {entities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FontAwesomeIcon icon={faEye} className="text-4xl mb-4 opacity-50" />
              <p>No entities selected. Select items in the multiselect to view/edit details.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {editedEntities.map((entity, index) => (
                console.log(entity),
                <Card key={entity.item_id || index} className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Entity Image (if available; placeholder otherwise) */}
                    {entity.item_image ? (
                      <img 
                        src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + entity.item_image} 
                        alt={entity.item_name} 
                        className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faImage} className="text-gray-400" />
                      </div>
                    )}
                    
                    {/* Entity Details Grid */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Read-only Fields */}
{/*                       <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Entity ID</label>
                        <Input 
                          value={entity.item_id} 
                          disabled 
                          className="bg-gray-50" 
                        />
                      </div>*/}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Entity Type</label>
                        <Input 
                          value={entity.entity_type} 
                          disabled 
                          className="bg-gray-50" 
                        />
                      </div> 
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Item Name</label>
                        <Input 
                          value={entity.item_name || entity.name || 'N/A'} 
                          disabled 
                          className="bg-gray-50" 
                        />
                      </div>
{/*                       <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">Item Image</label>
                        <Input 
                          value={entity.item_image || 'No image'} 
                          disabled 
                          className="bg-gray-50" 
                        />
                      </div> */}

                      {/* Editable Type-Specific Fields */}
                      {isReward ? (
                        <>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Is Rewardable</label>
                            <Select 
                              value={entity.is_rewardable ? 'true' : 'false'}
                              onValueChange={(val) => handleFieldChange(index, 'is_rewardable', val === 'true')}
                            >
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Rewardable Value</label>
                            <Input
                              type="number"
                              value={entity.rewardable_value ?? ''}
                              onChange={(e) => handleFieldChange(index, 'rewardable_value', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 10% - in percentage"
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600">Max Quantity Rewardable</label>
                            <Input
                              type="number"
                              value={entity.max_quantity_rewardable ?? ''}
                              onChange={(e) => handleFieldChange(index, 'max_quantity_rewardable', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 5 - in numbers"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Is Redeemable</label>
                            <Select 
                              value={entity.is_redeemable ? 'true' : 'false'}
                              onValueChange={(val) => handleFieldChange(index, 'is_redeemable', val === 'true')}
                            >
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Redeemable Value</label>
                            <Input
                              type="number"
                              value={entity.redeemable_value ?? ''}
                              onChange={(e) => handleFieldChange(index, 'redeemable_value', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 10% - in percentage"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Max Redeemable Value</label>
                            <Input
                              type="number"
                              value={entity.max_redeemable_value ?? ''}
                              onChange={(e) => handleFieldChange(index, 'max_redeemable_value', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 10% - in percentage"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Is Coins Distributable</label>
                            <Select 
                              value={entity.is_coins_distributable ? 'true' : 'false'}
                              onValueChange={(val) => handleFieldChange(index, 'is_coins_distributable', val === 'true')}
                            >
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </Select>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600">Max Quantity Redeemable</label>
                            <Input
                              type="number"
                              value={entity.max_quantity_redeemable ?? ''}
                              onChange={(e) => handleFieldChange(index, 'max_quantity_redeemable', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 5 - in numbers"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button onClick={handleSave} size="sm" disabled={entities.length === 0}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

// Update the DateInput component to handle proper date conversion
const DateInput = ({ value, onChange, placeholder = "DD/MM/YYYY", isToDate = false }) => {  // NEW: Add isToDate prop
  const [inputValue, setInputValue] = useState(value || '');
  const [showCalendar, setShowCalendar] = useState(false);

  const validateAndFormatDate = (input) => {
    let cleaned = input.replace(/[^\d/]/g, '');
    
    if (cleaned.length >= 2 && cleaned.indexOf('/') === -1) {
      cleaned = cleaned.substring(0, 2) + '/' + cleaned.substring(2);
    }
    if (cleaned.length >= 5 && cleaned.lastIndexOf('/') === 2) {
      cleaned = cleaned.substring(0, 5) + '/' + cleaned.substring(5);
    }
    
    cleaned = cleaned.substring(0, 10);
    return cleaned;
  };

  // UPDATED: Local convertToISODate in DateInput (returns YYYY-MM-DD, uses defaults)
  const convertToISODate = (displayDate, isToDate = false) => {
    if (!displayDate || !displayDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const today = new Date();
      if (isToDate) {
        today.setFullYear(today.getFullYear() + 1);
      }
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;  // YYYY-MM-DD (no T for input)
    }
    
    try {
      const [day, month, year] = displayDate.split('/');
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const dateObj = new Date(isoDate);
      if (dateObj.getFullYear() == year && dateObj.getMonth() == (month - 1) && dateObj.getDate() == day) {
        return isoDate;
      }
      // Invalid: default
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      const fallbackYear = today.getFullYear();
      const fallbackMonth = String(today.getMonth() + 1).padStart(2, '0');
      const fallbackDay = String(today.getDate()).padStart(2, '0');
      return `${fallbackYear}-${fallbackMonth}-${fallbackDay}`;
    } catch (error) {
      // Fallback to default
      const today = new Date();
      if (isToDate) today.setFullYear(today.getFullYear() + 1);
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  // Convert ISO date to DD/MM/YYYY for display
  const convertFromISODate = (isoDate) => {
    if (!isoDate) return '';
    
    try {
      if (isoDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        const [year, month, day] = isoDate.split('T')[0].split('-');
        return `${day}/${month}/${year}`;
      }
      return isoDate;
    } catch (error) {
      return isoDate;
    }
  };

  // UPDATED: Pre-populate if empty (now handles isToDate for 1-year default)
  useEffect(() => {
    let displayValue = convertFromISODate(value) || '';
    if (!displayValue && !value) {
      // Pre-populate fromDate with today OR toDate with 1 year ahead
      const defaultDate = new Date();
      if (isToDate) {
        defaultDate.setFullYear(defaultDate.getFullYear() + 1);  // NEW: 1 year ahead for toDate
      }
      displayValue = formatDateForDisplay(defaultDate.toISOString());  // DD/MM/YYYY
    }
    setInputValue(displayValue);
  }, [value, isToDate]);  // NEW: Add isToDate dependency

  const formatDateForInput = (displayDate) => {
    if (!displayDate) return '';
    
    try {
      if (displayDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = displayDate.split('/');
        if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
          return `${year}-${month}-${day}`;
        }
      }
      
      if (displayDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return displayDate;
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const handleTextInput = (e) => {
    const formatted = validateAndFormatDate(e.target.value);
    setInputValue(formatted);
    
    // Convert to YYYY-MM-DD (local format) before sending to parent
    const isoDate = convertToISODate(formatted, isToDate);  // UPDATED: Pass isToDate
    onChange(isoDate);
  };

  const handleCalendarInput = (e) => {
    const isoDate = e.target.value; // YYYY-MM-DD from calendar
    const displayDate = formatDateForDisplay(isoDate);
    setInputValue(displayDate);
    onChange(isoDate); // Send YYYY-MM-DD to parent
    setShowCalendar(false);
  };

  const dateInputRef = useRef(null);

  const handleButtonClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker(); // Modern browsers
      // Fallback for older browsers
      dateInputRef.current.focus();
      dateInputRef.current.click();
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={handleTextInput}
          placeholder={placeholder}
          maxLength="10"
          className="flex-1"
          icon={faCalendarAlt}
        />
        <div className="relative">
          <input
            ref={dateInputRef}
            type="date"
            className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
            value={formatDateForInput(inputValue)}
            onChange={handleCalendarInput}
          />
          <Button
            variant="outline"
            size="lg"
            className="p-4"
            onClick={handleButtonClick}
          >
            <FontAwesomeIcon 
              icon={faCalendarAlt} 
              className="text-gray-400"
            />
          </Button>
        </div>
      </div>
      {inputValue && inputValue.length === 10 && !convertToISODate(inputValue, isToDate) && (  // UPDATED: Pass isToDate
        <p className="text-xs text-red-500 mt-1 ml-1">
          Please enter a valid date (DD/MM/YYYY)
        </p>
      )}
    </div>
  );
};


// Reward Criteria Substep Component
const RewardCriteriaSubstep = ({ onNext, onCancel,formData, updateFormData, mode }) => {
  const { sharedData, setSharedData } = useRewardRuleContext();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([
    { id: "all", name: "All" },
    { id: "hyderabad", name: "Hyderabad" },
    { id: "vijayawada", name: "Vijayawada" },
    { id: "guntur", name: "Guntur" },
    { id: "visakhapatnam", name: "Visakhapatnam" }
  ]);


    const events = [
    { id: "sign up", name: "sign up" },
    { id: "first order", name: "first order" },
    { id: "festival offer", name: "festival offer" },
    //{ id: "order payment timeout", name: "order payment timeout" },
    { id: "invoice upload", name: "invoice upload" }
  ];

  const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

const customerTypes = [
  {id:"Builder",name:"Builder"},
  {id:"Individual",name:"Individual"},
  {id :"Contractor",name:"Contractor"}


]

  // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];

  const fields = [
    { label: "Category", value: "category", type: "select", options: categories },
    { label: "Brand", value: "brand", type: "select", options: brands },
    { label: "Amount", value: "amount" },
    { label: "Is Ecommerce", value: "is_ecommerce", type: "select", options: [
      { id: "true", name: "Yes" },
      { id: "false", name: "No" }
    ]},
    { label: "Campaign", value: "campaign" },
    { label: "Event", value: "event", type: "select", options: events },
    { label: "Location", value: "location", type: "multiselect", options: locations },
    { label: "Items", value: "items", type: "multiselect", options: items },
    { label: "Buyer Type", value:"customer_type", type:"select", options: customerTypes}
  ];


  // Use formData instead of local state
  const rewardCriteria = formData.rewardCriteria;

/*   const [rewardCriteria, setRewardCriteria] = useState([
    {
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: []
    }
  ]); */

   useEffect(() => {
      fetchCategories();
      fetchBrands();
      fetchLocations();
      fetchItemsForSharing();
    }, []);



      // Fetch Categories
  const fetchCategories = async () => {
    const token = sessionStorage.getItem('token');
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedCategories = (data.data).map(cat => ({
        id:cat._id,
        name:cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Brands
  const fetchBrands = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-all-vendors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
           'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedBrands = (data.data).map(brand => ({
        id:brand._id,
        name:brand.name
      }));
      setBrands(formattedBrands); 
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  //Fetch Items
 const fetchItems = async (categoryId, brandId) => {
  if (!categoryId || !brandId) {
    setItems([]);
    return;
  }

  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/get-all-items`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Items data:', data);
    
    const formattedItems = (data.data || []).map(item => ({
      id: item.item_id,
      name: item.item_name
    }));
    
    setItems(formattedItems);
  } catch (error) {
    console.error('Error fetching items:', error);
    setItems([]);
  } finally {
    setLoading(false);
  }
}; 

// Fetch items and share with other components (always fetch all, no category/brand dependency)
const fetchItemsForSharing = async () => {  // FIXED: No params needed
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/get-all-items`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Items data for sharing:', data);
    
    const formattedItems = [
      { 
        id: "all", 
        name: "All Items",
        item_id: "all",  // NEW: Consistent
        item_name: "All Items",
        item_image: '',  // NEW: Default empty for "All"
        entity_type: ''  // NEW: Default
      },
      ...(data.data || []).map(item => ({
        id: item.item_id,
        name: item.item_name,
        item_id: item.item_id,  // NEW: Full field
        item_name: item.item_name,  // NEW: Full field
        item_image: item.item_image || '',  // NEW: From API or empty
        entity_type: item.entity_type || ''  // NEW: From API or default
      }))
    ];
    
    // Share items with other components
    setSharedData(prev => ({ ...prev, fetchedItems: formattedItems }));
    setItems(formattedItems);
    
  } catch (error) {
    console.error('Error fetching items:', error);
    const fallbackItems = [{ 
      id: "all", 
      name: "All Items",
      item_id: "all",
      item_name: "All Items",
      item_image: '',
      entity_type: ''
    }];
    setSharedData(prev => ({ ...prev, fetchedItems: fallbackItems }));
    setItems(fallbackItems);
  } finally {
    setLoading(false);
  }
};


  //Fetch Locations
const fetchLocations = async () => {
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}organization/get-all-organizations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Locations data:', data);
    
    // Format locations with "All" option first, then actual locations
    const formattedLocations = [
      { id: "all", name: "All" }, // Keep the "All" option
      ...(data.data || []).map(location => ({
        id: location._id,
        name: location.city || location.name || location.organization_name // Use city field or fallback
      }))
    ];
    
    setLocations(formattedLocations);
  } catch (error) {
    console.error('Error fetching locations:', error);
    // Fallback to static locations if API fails
    setLocations([
      { id: "all", name: "All" },
      { id: "hyderabad", name: "Hyderabad" },
      { id: "vijayawada", name: "Vijayawada" },
      { id: "guntur", name: "Guntur" },
      { id: "visakhapatnam", name: "Visakhapatnam" }
    ]);
  } finally {
    setLoading(false);
  }
};

const generateRewardCriteriaName = (criteria) => {
  if (!criteria.conditions || criteria.conditions.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  // Filter out incomplete conditions
  const validConditions = criteria.conditions.filter(cond => 
    cond.field && 
    cond.operator && 
    (cond.value !== "" && cond.value !== null && cond.value !== undefined)
  );
  
  if (validConditions.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  const conditionStrings = validConditions.map(cond => {
    const fieldLabel = fields.find(f => f.value === cond.field)?.label || cond.field;
    const operatorLabel = operators.find(op => op.value === cond.operator)?.label || cond.operator;
    
    let valueString = "";
    
    if (cond.field === "location" && Array.isArray(cond.value)) {
      const locationNames = cond.value.map(locId => {
        const location = locations.find(loc => loc.id === locId);
        return location ? location.name : locId;
      });
      valueString = locationNames.length > 2 
        ? `${locationNames.slice(0, 2).join(", ")} and ${locationNames.length - 2} more` 
        : locationNames.join(", ");
    } else if (cond.field === "items" && Array.isArray(cond.value)) {
      const itemNames = cond.value.map(itemId => {
        const item = items.find(i => i.id === itemId);
        return item ? item.name : itemId;
      });
      valueString = itemNames.length > 2 
        ? `${itemNames.slice(0, 2).join(", ")} and ${itemNames.length - 2} more` 
        : itemNames.join(", ");
    } else if (cond.field === "category") {
      // Handle category field
      const category = categories.find(cat => cat.name === cond.value || cat.id === cond.value);
      valueString = category ? category.name : cond.value.toString();
    } else if (cond.field === "brand") {
      // Handle brand field
      const brand = brands.find(b => b.name === cond.value || b.id === cond.value);
      valueString = brand ? brand.name : cond.value.toString();
    } else if (cond.field === "amount") {
      valueString = `₹${cond.value}`;
    } else if (cond.field === "is_ecommerce") {
      valueString = cond.value === "true" || cond.value === true ? "Yes" : "No";
    } else if (Array.isArray(cond.value)) {
      valueString = cond.value.join(", ");
    } else {
      valueString = cond.value.toString();
    }
    
    return `${fieldLabel} ${operatorLabel} ${valueString}`;
  });
  
  if (conditionStrings.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  let baseStatement;
  if (conditionStrings.length === 1) {
    baseStatement = `When ${conditionStrings[0]}`;
  } else if (conditionStrings.length === 2) {
    baseStatement = `When ${conditionStrings[0]} and ${conditionStrings[1]}`;
  } else {
    baseStatement = `When ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings.length - 2} more conditions`;
  }

  const effectiveFrom = criteria.fromDate || formatDateForDisplay(new Date().toISOString());
  const effectiveTo = criteria.toDate || formatDateForDisplay(new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString());
  
  // Add date range if both dates are present
  if (criteria.fromDate || criteria.toDate) {
    baseStatement += `, applies between ${effectiveFrom} and ${effectiveTo}`;
  } else {
    baseStatement += `, applies from ${effectiveFrom} (ongoing)`;
  }

  if (criteria.frequency && criteria.frequency > 0) {
    const periodLabel = {
      transaction: 'transaction',
      daily: 'day',
      weekly: 'week',
      monthly: 'month',
      lifetime: 'lifetime'
    }[criteria.period] || criteria.period;
    const scopeLabel = {
      customer: 'customer',
      project: 'project',
      global: 'global'
    }[criteria.scope] || criteria.scope;
    baseStatement += ` (up to ${criteria.frequency} times per ${periodLabel} for ${scopeLabel})`;
  }
  
  return baseStatement;
};


  // CRUD functions
/* const updateRewardCriteriaName = (criteriaId, name) => {
    setRewardCriteria(prev => 
      prev.map(criteria => 
        criteria.id === criteriaId ? { ...criteria, name } : criteria
      )
    );
  }; */

  const updateRewardCriteriaName = (criteriaId, name) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId ? { ...criteria, name } : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

/* const updateRewardCriteriaFromDate = (criteriaId, fromDate) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = { ...criteria, fromDate };
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */


const updateRewardCriteriaFromDate = (criteriaId, fromDate) => {  // fromDate is YYYY-MM-DD from DateInput
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updated = { ...criteria, fromDate };  // Store YYYY-MM-DD (will convert to ISO on save)
      // Regenerate name with effective dates
      //updated.name = updated.name || generateRewardCriteriaName(updated);  // Use existing generate (updated in point 4)
      return updated;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};


/*  const updateRewardCriteriaToDate = (criteriaId, toDate) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = { ...criteria, toDate };
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */

const updateRewardCriteriaToDate = (criteriaId, toDate) => {  // toDate is YYYY-MM-DD
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updated = { ...criteria, toDate };
      // Regenerate name with effective dates
      //updated.name = updated.name || generateRewardCriteriaName(updated);
      return updated;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};

  /* const addRewardCriteriaCondition = (criteriaId) => {
    setRewardCriteria(prev => 
      prev.map(criteria => 
        criteria.id === criteriaId 
          ? { 
              ...criteria, 
              conditions: [...criteria.conditions, { 
                id: uuidv4(), 
                field: "", 
                operator: "==", 
                value: "" 
              }] 
            }
          : criteria
      )
    );
  };
 */

  const addRewardCriteriaCondition = (criteriaId) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId 
        ? { 
            ...criteria, 
            conditions: [...criteria.conditions, { 
              id: uuidv4(), 
              field: "", 
              operator: "==", 
              value: "" ,
            conditionalOperator: criteria.conditions.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
            }] 
          }
        : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

   // Enhanced updateRewardCriteriaCondition to track and share category and brand selection
/*   const updateRewardCriteriaCondition = (criteriaId, condId, key, value) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = {
            ...criteria,
            conditions: criteria.conditions.map(cond => {
              if (cond.id === condId) {
                const updatedCond = { ...cond, [key]: value };
                
                // Track and share category and brand selection
                if (key === 'value') {
                  if (cond.field === 'category') {
                    const selectedCategory = categories.find(cat => cat.name === value);
                    if (selectedCategory) {
                      setSharedData(prev => ({ 
                        ...prev, 
                        selectedCategory: selectedCategory 
                      }));
                    }
                  } else if (cond.field === 'brand') {
                    const selectedBrand = brands.find(brand => brand.name === value);
                    if (selectedBrand) {
                      setSharedData(prev => ({ 
                        ...prev, 
                        selectedBrand: selectedBrand 
                      }));
                    }
                  }
                }
                
                return updatedCond;
              }
              return cond;
            })
          };
          
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */

const updateRewardCriteriaCondition = (criteriaId, condId, key, value) => {
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updatedCriteria = {
        ...criteria,
        conditions: criteria.conditions.map(cond => {
          if (cond.id === condId) {
            const updatedCond = { ...cond, [key]: value };
            
            // Track and share category and brand selection
            if (key === 'value') {
              if (cond.field === 'category') {
                const selectedCategory = categories.find(cat => cat.name === value || cat.id === value);
                if (selectedCategory) {
                  setSharedData(prev => ({ 
                    ...prev, 
                    selectedCategory: selectedCategory 
                  }));
                }
              } else if (cond.field === 'brand') {
                const selectedBrand = brands.find(brand => brand.name === value || brand.id === value);
                if (selectedBrand) {
                  setSharedData(prev => ({ 
                    ...prev, 
                    selectedBrand: selectedBrand 
                  }));
                }
              }
            }
            
            return updatedCond;
          }
          return cond;
        })
      };
      
      // Always regenerate name when conditions change
      //updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
      return updatedCriteria;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};

  // Function to update criteria-level conditional operator
  const updateCriteriaConditionalOperator = (criteriaId, operator) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId 
        ? { ...criteria, nextOperator: operator }
        : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

  /* const removeRewardCriteriaCondition = (criteriaId, condId) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = {
            ...criteria,
            conditions: criteria.conditions.filter(cond => cond.id !== condId)
          };
          // Auto-generate name after removing condition
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */

  const updateRewardCriteriaFrequency = (criteriaId, frequency) => {
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updated = { ...criteria, frequency: frequency ? Number(frequency) : null };
      // Regenerate name if changed
      //updated.name = generateRewardCriteriaName(updated);
      return updated;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};

const updateRewardCriteriaPeriod = (criteriaId, period) => {
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updated = { ...criteria, period };
      // Regenerate name if changed
      //updated.name = generateRewardCriteriaName(updated);
      return updated;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};  

const updateRewardCriteriaScope = (criteriaId, scope) => {
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updated = { ...criteria, scope };
      // Regenerate name if changed
      //updated.name = generateRewardCriteriaName(updated);
      return updated;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};

   const removeRewardCriteriaCondition = (criteriaId, condId) => {
    const updatedCriteria = rewardCriteria.map(criteria => {
      if (criteria.id === criteriaId) {
        const updatedCriteria = {
          ...criteria,
          conditions: criteria.conditions.filter(cond => cond.id !== condId)
        };
        // Auto-generate name after removing condition
        if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
          //updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
        }
        return updatedCriteria;
      }
      return criteria;
    });
    updateFormData('rewardCriteria', updatedCriteria);
  };


  /* const addRewardCriteria = () => {
    setRewardCriteria(prev => [...prev, { 
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: [] 
    }]);
  }; */

  const addRewardCriteria = () => {
    const updatedCriteria = [...rewardCriteria, { 
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: [] ,
      nextOperator: rewardCriteria.length > 0 ? "AND" : null // Add conditional operator for non-first criteria
    }];    updateFormData('rewardCriteria', updatedCriteria);
  };

  /* const removeRewardCriteria = (criteriaId) => {
    setRewardCriteria(prev => prev.filter(criteria => criteria.id !== criteriaId));
  }; */

  const removeRewardCriteria = (criteriaId) => {
    const updatedCriteria = rewardCriteria.filter(criteria => criteria.id !== criteriaId);
    updateFormData('rewardCriteria', updatedCriteria);
  };

  useEffect(() => {
  if (sharedData.selectedBrand && !sharedData.selectedCategory) {
    fetchCategoriesForBrand(sharedData.selectedBrand.id);
  }

  if (sharedData.selectedCategory && !sharedData.selectedBrand) {
    fetchBrandsForCategory(sharedData.selectedCategory.id);
  }

  if (sharedData.selectedCategory && sharedData.selectedBrand) {
    fetchItemsForSharing(sharedData.selectedCategory.id, sharedData.selectedBrand.id);
  }
}, [sharedData.selectedBrand, sharedData.selectedCategory]);


const fetchCategoriesForBrand = async (brandId) => {
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-productCategoriesBy-vendor_id?vendor_id=${brandId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      }
    });

    const data = await res.json();
    const formatted = data.data.map(cat => ({
      id: cat._id,
      name: cat.product_category_name
    }));
    setCategories([{ id: "all", name: "All" }, ...formatted]);
  } catch (err) {
    console.error("Error fetching categories for brand", err);
  } finally {
    setLoading(false);
  }
};

const fetchBrandsForCategory = async (categoryId) => {
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productCategoryId?product_category_id=${categoryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      }
    });

    const data = await res.json();
    const formatted = data.data.map(brand => ({
      id: brand._id,
      name: brand.name
    }));
    setBrands([{ id: "all", name: "All" }, ...formatted]);
  } catch (err) {
    console.error("Error fetching brands for category", err);
  } finally {
    setLoading(false);
  }
};



  // Enhanced renderConditions function with conditional operators
  const renderConditions = (conditions, onUpdate, onRemove, parentId, onAdd) => (
    <div className="space-y-3">
      {conditions.map((cond, index) => {
        const selectedField = fields.find(f => f.value === cond.field);
        
        return (
          <div key={cond.id} className="space-y-3">
            {/* Conditional Operator Dropdown - Show for all conditions except the first */}
            {index > 0 && (
  <div className="flex justify-center">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
      <Select 
        value={conditions[index - 1]?.conditionalOperator || "AND"} 
        onValueChange={(value) => onUpdate(parentId, conditions[index - 1].id, "conditionalOperator", value)}
      >
        {conditionalOperators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            <div className="flex flex-col">
              <span className="font-medium">{op.label}</span>
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  </div>
)}

            
            {/* Condition Row */}
            <div className="flex gap-3 items-start p-2 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
                  <Select 
                    value={cond.field} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "field", value)}
                    searchable={true}
                    placeholder="Select Field"
                  >
                    {fields.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
                  <Select 
                    value={cond.operator} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "operator", value)}
                    searchable={true}
                    placeholder="Select Operator"
                  >
                    {operators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
                  {selectedField?.type === "multiselect" ? (
                    <MultiSelectDropdown
                      options={selectedField.options}
                      selectedValues={Array.isArray(cond.value) ? cond.value : (cond.value ? [cond.value] : [])}
                      onChange={(selectedValues) => onUpdate(parentId, cond.id, "value", selectedValues)}
                      placeholder={`Select ${selectedField.label}`}
                    />
                  ) : selectedField?.type === "select" ? (
                    <Select 
                      value={cond.value} 
                      onValueChange={(value) => onUpdate(parentId, cond.id, "value", value)}
                      searchable={true}
                      placeholder="Select Value"
                    >
                      {selectedField.options.map((option) => (
                        <SelectItem key={option.id} value={option.name}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={selectedField?.value === "amount" ? "number" : "text"}
                      value={cond.value}
                      onChange={(e) => onUpdate(parentId, cond.id, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  )}
                </div>
              </div>
              
              <div className="pt-6">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(parentId, cond.id)}
                  className="w-8 h-8 p-0 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}    
      <Button 
        variant="outline"
        onClick={() => onAdd(parentId)} 
        disabled={loading}
        className="w-full py-1 border-dashed border-2 hover:border-[#f7525a] hover:text-[#f7525a] transition-all duration-200"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {loading ? "Loading..." : "Add Condition"}
      </Button>
    </div>
  );


return (
  <Card className="w-full">
    <CardContent className="p-2">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-md font-bold text-gray-900 mb-1">When do you want to give coins?</h3>
          {/* <p className="text-gray-600">Define the criteria and conditions for rewarding coins</p> */}
        </div>
        <Button onClick={addRewardCriteria} className="shadow-lg p-1">
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Criteria
        </Button>
      </div>
      
      <div className="space-y-6">
        {rewardCriteria.map((criteria, criteriaIndex) => (
          <div key={criteria.id}>
            {/* Conditional Operator Between Criteria - Show for all criteria except the first */}
            {criteriaIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={rewardCriteria[criteriaIndex - 1]?.nextOperator || "AND"} 
                    onValueChange={(value) =>
          updateCriteriaConditionalOperator(rewardCriteria[criteriaIndex - 1].id, value)
        }
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {/* Criteria Card */}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={criteria.name /* || generateRewardCriteriaName(criteria) */}
                    onChange={(e) => updateRewardCriteriaName(criteria.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {rewardCriteria.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRewardCriteria(criteria.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              {/* Date Range Section */}
              <div className="mb-6">
{/*                 <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Date Range
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      From Date
                    </label>
                    <DateInput
                      value={criteria.fromDate}
                      onChange={(date) => updateRewardCriteriaFromDate(criteria.id, date)}
                      placeholder="DD/MM/YYYY"
                      isToDate={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      To Date
                    </label>
                    <DateInput
                      value={criteria.toDate}
                      onChange={(date) => updateRewardCriteriaToDate(criteria.id, date)}
                      placeholder="DD/MM/YYYY"
                      isToDate={true}
                    />
                  </div>
                </div>
              </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                Applicability Settings
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Frequency (times)
                  </label>
                  <Input
                    type="number"
                    placeholder="Unlimited (leave empty)"
                    value={criteria.frequency || ''}
                    onChange={(e) => updateRewardCriteriaFrequency(criteria.id, e.target.value)}
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 or empty = unlimited</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Period
                  </label>
                  <Select
                    value={criteria.period || 'lifetime'}
                    onValueChange={(value) => updateRewardCriteriaPeriod(criteria.id, value)}
                    placeholder="Select Period"
                  >
                    <SelectItem value="transaction">Per Transaction</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="lifetime">Lifetime</SelectItem>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Scope
                  </label>
                  <Select
                    value={criteria.scope || 'customer'}
                    onValueChange={(value) => updateRewardCriteriaScope(criteria.id, value)}
                    placeholder="Select Scope"
                  >
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                    <SelectItem value="global">Global</SelectItem>
                  </Select>
                </div>
              </div>
            </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Conditions
                </label>
                {renderConditions(
                  criteria.conditions,
                  updateRewardCriteriaCondition,
                  removeRewardCriteriaCondition,
                  criteria.id,
                  addRewardCriteriaCondition
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-4 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
        <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

};

// Reward Rules Substep Component - FIXED
const RewardRulesSubstep = ({ onNext,onCancel ,onBack,formData, updateFormData, mode  }) => {
  const { sharedData } = useRewardRuleContext();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [editedRewardEntities, setEditedRewardEntities] = useState([]);
  // Use formData instead of local state
  const rewardRules = formData.rewardRules;



    //Fetch Items
const fetchItems = async () => {

  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    setLoading(true);
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/get-all-items`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Items data:', data);
    
    const formattedItems = (data.data || []).map(item => ({
      id: item.item_id,
      name: item.item_name
    }));
    
    setItems(formattedItems);
  } catch (error) {
    console.error('Error fetching items:', error);
    setItems([]);
  } finally {
    setLoading(false);
  }
};



    // Use shared items from RewardCriteriaSubstep
const availableItems = sharedData.fetchedItems.length > 0 
  ? sharedData.fetchedItems 
  : [{ id: "all", name: "All Items" }]; // Fallback

const generateRewardRuleName = (rule) => {
  if (!rule.rewardType || (!rule.rewardValue && rule.rewardValue !== 0)) {
    return "New Reward Rule";
  }

  // Convert rewardValue to number if it's a string
  const rewardValue = typeof rule.rewardValue === 'string' ? parseFloat(rule.rewardValue) : rule.rewardValue;

  // Format reward amount based on reward type
  let rewardAmount = "";
  if (rule.rewardType === "fixed") {
    rewardAmount = `${rewardValue} coins`;
  } else if (rule.rewardType === "percentage") {
    rewardAmount = `${rewardValue}% coins`;
  }

  // Format what it's applied on
  let appliedOn = "";
  if (rule.applyTarget === "order") {
    appliedOn = "order value";
  } else if (rule.applyTarget === "item") {
    if (rule.applicableItems && rule.applicableItems.length > 0) {
      // Filter out "all" items if present
      const validItems = rule.applicableItems.filter(item =>
        item && (typeof item === 'object' ? item.id !== "all" : item !== "all")
      );
      
      if (validItems.length === 0) {
        appliedOn = "all items";
      } else if (validItems.length === 1) {
        const itemName = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        appliedOn = itemName || 'selected item';
      } else if (validItems.length === 2) {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'} and ${item2Name || 'item'}`;
      } else {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'}, ${item2Name || 'item'} and ${validItems.length - 2} more items`;
      }
    } else {
      appliedOn = "selected items";
    }
  }

  // Format destination
  let destination = "";
  if (rule.destination === "wallet") {
    destination = "wallet";
  } else if (rule.destination === "immediate") {
    destination = "immediate order discount";
  }

  // Format limit based on limit type
  let limitText = "";
  if (rule.limit && parseFloat(rule.limit) > 0) {
    const limitValue = parseFloat(rule.limit);
    
    if (rule.limitType === "fixed") {
      limitText = ` (max ${limitValue} coins per order)`;
    } else if (rule.limitType === "percentage") {
      if (rule.applyTarget === "order") {
        limitText = ` (max ${limitValue}% of order value)`;
      } else {
        limitText = ` (max ${limitValue}% of item value)`;
      }
    }
  }

  // Construct the final name
  let ruleName = "";
  
  if (rule.applyTarget === "order") {
    ruleName = `Give ${rewardAmount} on ${appliedOn} to ${destination}${limitText}`;
  } else if (rule.applyTarget === "item") {
    ruleName = `Give ${rewardAmount} on ${appliedOn} to ${destination}${limitText}`;
  } else {
    // Fallback for undefined applyTarget
    ruleName = `Give ${rewardAmount} to ${destination}${limitText}`;
  }

  return ruleName;
};



/*   const updateRewardRule = (ruleId, key, value) => {
    setRewardRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, [key]: value };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateRewardRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

 const updateRewardRule = (ruleId, key, value) => {
  const updatedRules = rewardRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, [key]: value };
      // Always regenerate name when any field changes (except when explicitly setting the name)
      if (key !== 'name') {
        //updatedRule.name = generateRewardRuleName(updatedRule);
      }
      return updatedRule;
    }
    return rule;
  });
  updateFormData('rewardRules', updatedRules);
};


/*   const updateRewardRuleApplicableItems = (ruleId, selectedValues) => {
    const selectedItems = selectedValues.map(itemId => {
      const item = availableItems.find(item => item.id === itemId);
      return item ? { id: item.id, name: item.name } : null;
    }).filter(Boolean);

    setRewardRules(prev => 
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, applicableItems: selectedItems } : rule
      )
    );
  }; */

const updateRewardRuleApplicableItems = (ruleId, selectedValues) => {
  // Get current rule to preserve user-edited values
  const currentRule = rewardRules.find(r => r.id === ruleId);
  const existingItems = currentRule?.applicableItems || [];
  
  const selectedItems = selectedValues.map(itemId => {
    const item = availableItems.find(item => item.id === itemId);
    if (!item) return null;
    
    // FIXED: Preserve existing user-edited values for this item
    const existingEntity = existingItems.find(e => 
      (e.item_id === itemId || e.item_id === item.id || e.id === itemId)
    );
    
    return {
      item_id: item.item_id || item.id,
      entity_type: item.entity_type || '',
      item_name: item.item_name || item.name,
      item_image: item.item_image || '',
      // FIXED: Preserve user-edited reward values if they exist
      is_rewardable: existingEntity?.is_rewardable !== undefined ? existingEntity.is_rewardable : (item.is_rewardable !== undefined ? item.is_rewardable : true),
      rewardable_value: existingEntity?.rewardable_value !== undefined && existingEntity?.rewardable_value !== null ? existingEntity.rewardable_value : (item.rewardable_value || 0),
      max_quantity_rewardable: existingEntity?.max_quantity_rewardable !== undefined && existingEntity?.max_quantity_rewardable !== null ? existingEntity.max_quantity_rewardable : (item.max_quantity_rewardable || 0),
      // FIXED: Preserve user-edited redeem values if they exist
      is_redeemable: existingEntity?.is_redeemable !== undefined ? existingEntity.is_redeemable : (item.is_redeemable !== undefined ? item.is_redeemable : false),
      redeemable_value: existingEntity?.redeemable_value !== undefined && existingEntity?.redeemable_value !== null ? existingEntity.redeemable_value : (item.redeemable_value || 0),
      max_redeemable_value: existingEntity?.max_redeemable_value !== undefined && existingEntity?.max_redeemable_value !== null ? existingEntity.max_redeemable_value : (item.max_redeemable_value || 0),
      is_coins_distributable: existingEntity?.is_coins_distributable !== undefined ? existingEntity.is_coins_distributable : (item.is_coins_distributable !== undefined ? item.is_coins_distributable : false),
      max_quantity_redeemable: existingEntity?.max_quantity_redeemable !== undefined && existingEntity?.max_quantity_redeemable !== null ? existingEntity.max_quantity_redeemable : (item.max_quantity_redeemable || 0),
    };
  }).filter(Boolean);
  const updatedRules = rewardRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, applicableItems: selectedItems };
      //updatedRule.name = generateRewardRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('rewardRules', updatedRules);
};

  // Function to update reward-level conditional operator
  const updateRewardRulesConditionalOperator = (ruleId, operator) => {
    const updatedRules = rewardRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('rewardRules', updatedRules);
  };

/*   const addRewardRule = () => {
    setRewardRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      rewardType: "fixed", 
      rewardValue: 0, 
      applyTarget: "order",
      destination: "wallet",
      limit: 0,
      applicableItems: [] 
    }]);
  }; */

  const addRewardRule = () => {
    const updatedRules = [...rewardRules, { 
      id: uuidv4(), 
      name: "", 
      rewardType: "fixed", 
      rewardValue: 0, 
      applyTarget: "order",
      destination: "wallet",
      limit: 0,
      limitType: "fixed",
      applicableItems: [],
      nextOperator:rewardRules.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
    }];
    updateFormData('rewardRules', updatedRules);
  };

  /* const removeRewardRule = (ruleId) => {
    setRewardRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

  const removeRewardRule = (ruleId) => {
    const updatedRules = rewardRules.filter(rule => rule.id !== ruleId);
    updateFormData('rewardRules', updatedRules);
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-1">How many coins can be given?</h3>
{/*             <p className="text-gray-600">Configure the reward amounts and limits</p>
 */}            {/* Show selected category and brand info */}
          </div>
          <Button onClick={addRewardRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Reward Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {rewardRules.map((rule,ruleIndex) => (
            <div key={rule.id} >
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name /* || generateRewardRuleName(rule) */}
                    onChange={(e) => updateRewardRule(rule.id, "name", e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {rewardRules.length > 1 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRewardRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div className="space-y-6">
                <div>
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Reward Configuration
                  </label> */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Reward Type
                      </label>
                      {/* FIXED: Use new Select component structure */}
                      <Select 
                        value={rule.rewardType} 
                        onValueChange={(value) => updateRewardRule(rule.id, "rewardType", value)}
                        placeholder="Select Reward Type"
                      >
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Reward Value
                      </label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={rule.rewardValue}
                        onChange={(e) => updateRewardRule(rule.id, "rewardValue", e.target.value)}
                      />
                    </div>
                                      <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded On
                    </label>
                    {/* FIXED: Use new Select component structure */}
                    <Select 
                      value={rule.applyTarget} 
                      onValueChange={(value) => updateRewardRule(rule.id, "applyTarget", value)}
                      placeholder="Select Apply Target"
                    >
                      <SelectItem value="order">Order</SelectItem>
                      <SelectItem value="item">Item</SelectItem>
                      <SelectItem value="sign up">Sign Up</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded To
                    </label>
                    {/* FIXED: Use new Select component structure */}
                    <Select 
                      value={rule.destination} 
                      onValueChange={(value) => updateRewardRule(rule.id, "destination", value)}
                      placeholder="Select Destination"
                    >
                      <SelectItem value="wallet">Wallet</SelectItem>
                      <SelectItem value="immediate">Immediate Order</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded Items
                    </label>
                    <div className="flex gap-2">  
                      <MultiSelectDropdown
                        options={availableItems}
                        selectedValues={rule.applicableItems ? 
                          rule.applicableItems.map(item => 
                            typeof item === 'object' ? item.item_id || item.id : item
                          ).filter(id => id !== "all") : []
                        }
                        onChange={(selectedValues) => updateRewardRuleApplicableItems(rule.id, selectedValues)}  // FIXED: Pass rule.id
                        placeholder="Select items"
                      />
                      {/* FIXED: Eye button to open modal */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditedRewardEntities(rule.applicableItems || []);
                          setShowRewardModal(true);
                        }}
                        className="w-8 h-8 p-0 flex-shrink-0"
                        title="View/Edit Entity Details"
                      >
                        <FontAwesomeIcon icon={faEye} className="text-gray-500 text-sm" />
                      </Button>
                    </div>
                  </div>

                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="Max limit"
                        value={rule.limit}
                        onChange={(e) => updateRewardRule(rule.id, "limit", e.target.value)}
                      />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Limit Type</label>
                    <Select 
                        value={rule?.limitType}
                      onChange={(value) => updateRewardRule(rule.id, 'limitType', value)}
                        placeholder="Select Limit Type"
                      >
                        <SelectItem value="">Select Limit Type</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
        {showRewardModal && (
        <EntityDetailsModal
          isOpen={showRewardModal}
          onClose={() => setShowRewardModal(false)}
          entities={editedRewardEntities}
          type="reward"
          onSave={(updatedEntities) => {
            setEditedRewardEntities(updatedEntities);
            // FIXED: Update formData with edited entities
            const updatedRules = rewardRules.map(rule => {
              if (rule.id === rule.id) {  // Assuming current rule; pass rule.id if multi
                return { ...rule, applicableItems: updatedEntities };
              }
              return rule;
            });
            updateFormData('rewardRules', updatedRules);
          }}
        />
      )}
      </CardContent>
    </Card>
  );
};


// Applicability Rules Substep Component
const ApplicabilityRulesSubstep = ({ onNext,onCancel, onBack,formData, updateFormData, mode  }) => {
  const { sharedData, setSharedData } = useRewardRuleContext();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
const [filteredBrands, setFilteredBrands] = useState([]);

  const [locations, setLocations] = useState([
    { id: "all", name: "All" },
    { id: "hyderabad", name: "Hyderabad" },
    { id: "vijayawada", name: "Vijayawada" },
    { id: "guntur", name: "Guntur" },
    { id: "visakhapatnam", name: "Visakhapatnam" }
  ]);

   const events = [
    //{ id: "sign up", name: "sign up" },
    { id: "first order", name: "first order" },
    //{ id: "festival offer", name: "festival offer" },
    { id: "order payment timeout", name: "order payment timeout" },
    //{ id: "invoice upload", name: "invoice upload" }
  ];

  const customerTypes = [
  {id:"Builder",name:"Builder"},
  {id:"Individual",name:"Individual"},
  {id :"Contractor",name:"Contractor"}


]

useEffect(() => {
  const selectedCategoryName = getSelectedValue('category');
  const selectedBrandName = getSelectedValue('brand');

  const selectedCategory = categories.find(c => c.name === selectedCategoryName);
  const selectedBrand = brands.find(b => b.name === selectedBrandName);

  if (selectedCategory && selectedBrand) {
    // Optional: If you want to load items for this combo
    // fetchItemsByCategoryAndBrand(selectedCategory.id, selectedBrand.id);
  }

  if (selectedCategory && !selectedBrand) {
    filterBrandsByCategory(selectedCategory.id);
  } else if (!selectedCategory && selectedBrand) {
    filterCategoriesByBrand(selectedBrand.id);
  } else {
    setFilteredCategories(categories);
    setFilteredBrands(brands);
  }
}, [formData.applicabilityRules, categories, brands]);


const getSelectedValue = (fieldKey) => {
  const allConditions = formData.applicabilityRules.flatMap(rule => rule.conditions);
  const found = [...allConditions].reverse().find(cond => cond.field === fieldKey && cond.value);
  return found?.value || null;
};



const filterBrandsByCategory = async (categoryId) => {
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productCategoryId?product_category_id=${categoryId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(b => ({ id: b._id, name: b.name }));
    setFilteredBrands(formatted);
  } catch (err) {
    console.error('Error filtering brands by category:', err);
  }
};

const filterCategoriesByBrand = async (brandId) => {
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-productCategoriesBy-vendor_id?vendor_id=${brandId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(c => ({ id: c._id, name: c.product_category_name }));
    setFilteredCategories(formatted);
  } catch (err) {
    console.error('Error filtering categories by brand:', err);
  }
};


  const fields = [
    { label: "Category", value: "category", type: "select", options: filteredCategories.length > 0 ? filteredCategories : categories },
  { label: "Brand", value: "brand", type: "select", options: filteredBrands.length > 0 ? filteredBrands : brands },
    { label: "Amount", value: "amount" },
    { label: "Is Ecommerce", value: "is_ecommerce", type: "select", options: [
      { id: "true", name: "Yes" },
      { id: "false", name: "No" }
    ]},
    { label: "Campaign", value: "campaign" },
    { label: "Event", value: "event", type: "select", options: events },
    { label: "Location", value: "location", type: "multiselect", options: locations },
    { label: "Items", value: "items", type: "multiselect", options: items },
    { label: "Buyer Type", value:"customer_type", type:"select", options: customerTypes}
  ];

  const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

  // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];
  
  // Use formData instead of local state
  const applicabilityRules = formData.applicabilityRules;

  /* const [applicabilityRules, setApplicabilityRules] = useState([
    { id: uuidv4(), name: "", conditions: [] }
  ]); */

  // Add useEffect to fetch data
  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchLocations();
    fetchItemsForSharing();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedCategories = (data.data).map(cat => ({
        id: cat._id,
        name: cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Brands
  const fetchBrands = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-all-vendors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedBrands = (data.data).map(brand => ({
        id: brand._id,
        name: brand.name
      }));
      setBrands(formattedBrands); 
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Locations
  const fetchLocations = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}organization/get-all-organizations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedLocations = [
        { id: "all", name: "All" },
        ...(data.data || []).map(location => ({
          id: location._id,
          name: location.city || location.name || location.organization_name
        }))
      ];
      
      setLocations(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([
        { id: "all", name: "All" },
        { id: "hyderabad", name: "Hyderabad" },
        { id: "vijayawada", name: "Vijayawada" },
        { id: "guntur", name: "Guntur" },
        { id: "visakhapatnam", name: "Visakhapatnam" }
      ]);
    } finally {
      setLoading(false);
    }
  };


    // FIXED: fetchItemsForSharing - Exactly like in RewardCriteriaSubstep (fetch all items unconditionally)
  const fetchItemsForSharing = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/get-all-items`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Items data for sharing (Applicability):', data);  // FIXED: Log for debugging
      
      const formattedItems = [
      { 
        id: "all", 
        name: "All Items",
        item_id: "all",  // NEW: Consistent
        item_name: "All Items",
        item_image: '',  // NEW: Default empty for "All"
        entity_type: ''  // NEW: Default
      },
      ...(data.data || []).map(item => ({
        id: item.item_id,
        name: item.item_name,
        item_id: item.item_id,  // NEW: Full field
        item_name: item.item_name,  // NEW: Full field
        item_image: item.item_image || '',  // NEW: From API or empty
        entity_type: item.entity_type || ''  // NEW: From API or default
      }))
    ];
      
      // Share items with other components (e.g., RedemptionRulesSubstep)
      setSharedData(prev => ({ ...prev, fetchedItems: formattedItems }));
      setItems(formattedItems);  // Also set local for "Items" conditions in this step
      
    } catch (error) {
      console.error('Error fetching items (Applicability):', error);
       const fallbackItems = [{ 
      id: "all", 
      name: "All Items",
      item_id: "all",
      item_name: "All Items",
      item_image: '',
      entity_type: ''
    }];
    setSharedData(prev => ({ ...prev, fetchedItems: fallbackItems }));
    setItems(fallbackItems);
    } finally {
      setLoading(false);
    }
  };


  const generateApplicabilityRuleName = (rule) => {
  if (!rule.conditions || rule.conditions.length === 0) {
    return "New Redeem Criteria";
  }
  
  const conditionStrings = rule.conditions.map(cond => {
    if (!cond.field || !cond.operator || (!cond.value && cond.value !== false && cond.value !== 0)) return "";
    
    const fieldLabel = fields.find(f => f.value === cond.field)?.label || cond.field;
    const operatorLabel = operators.find(op => op.value === cond.operator)?.label || cond.operator;
    
    let valueString = "";
    if (cond.field === "location" && Array.isArray(cond.value)) {
      const locationNames = cond.value.map(locId => {
        const location = locations.find(loc => loc.id === locId);
        return location ? location.name : locId;
      });
      valueString = locationNames.length > 2 
        ? `${locationNames.slice(0, 2).join(", ")} and ${locationNames.length - 2} more` 
        : locationNames.join(", ");
    } else if (cond.field === "items" && Array.isArray(cond.value)) {
      const itemNames = cond.value.map(itemId => {
        const item = items.find(i => i.id === itemId);
        return item ? item.name : itemId;
      });
      valueString = itemNames.length > 2 
        ? `${itemNames.slice(0, 2).join(", ")} and ${itemNames.length - 2} more` 
        : itemNames.join(", ");
    } else if (cond.field === "amount") {
      valueString = `₹${cond.value}`;
    } else if (cond.field === "is_ecommerce") {
      valueString = cond.value === "Yes" ? "Yes" : "No";
    } else if (cond.field === "category") {
      valueString = cond.value;
    } else if (cond.field === "brand") {
      valueString = cond.value;
    } else if (cond.field === "campaign") {
      valueString = cond.value;
    } else if (cond.field === "event") {
      valueString = cond.value;
    } else {
      valueString = cond.value.toString();
    }
    
    return `${fieldLabel} ${operatorLabel} ${valueString}`;
  }).filter(str => str !== "");
  
  if (conditionStrings.length === 0) {
    return "New Redeem Criteria";
  }
  
  
  // Create the applicability statement
  let applicabilityStatement = "";
  if (conditionStrings.length === 1) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}`;
  } else if (conditionStrings.length === 2) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]} and ${conditionStrings[1]}`;
  } else if (conditionStrings.length === 3) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings[2]}`;
  } else {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings.length - 2} more conditions are met`;
  }

    const effectiveFrom = rule.fromDate || formatDateForDisplay(new Date().toISOString());
  const effectiveTo = rule.toDate || formatDateForDisplay(new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString());
  
  applicabilityStatement += `, applies between ${effectiveFrom} and ${effectiveTo}`;


  if (rule.frequency && rule.frequency > 0) {
    const periodLabel = {
      transaction: 'transaction',
      daily: 'day',
      weekly: 'week',
      monthly: 'month',
      lifetime: 'lifetime'
    }[rule.period] || rule.period;
    const scopeLabel = {
      customer: 'customer',
      project: 'project',
      global: 'global'
    }[rule.scope] || rule.scope;
    applicabilityStatement += ` (up to ${rule.frequency} times per ${periodLabel} for ${scopeLabel})`;
  }
  
  return applicabilityStatement;
};

  /* const addApplicabilityCondition = (ruleId) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: [...rule.conditions, { 
                id: uuidv4(), 
                field: "", 
                operator: "==", 
                value: "" 
              }]
            }
          : rule
      )
    );
  }; */


  const addApplicabilityCondition = (ruleId) => {
    const updatedRules = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: [...rule.conditions, { 
              id: uuidv4(), 
              field: "", 
              operator: "==", 
              value: "" ,
              conditionalOperator: rule.conditions.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
            }]
          }
        : rule
    );
    updateFormData('applicabilityRules', updatedRules);
  };

 /*  const updateApplicabilityCondition = (ruleId, condId, key, value) => {
    setApplicabilityRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = {
            ...rule,
            conditions: rule.conditions.map(cond => 
              cond.id === condId ? { ...cond, [key]: value } : cond
            )
          };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateApplicabilityRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

const updateApplicabilityCondition = (ruleId, condId, key, value) => {
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = {
        ...rule,
        conditions: rule.conditions.map(cond => 
          cond.id === condId ? { ...cond, [key]: value } : cond
        )
      };
      // Always regenerate name when conditions change
      //updatedRule.name = generateApplicabilityRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};


  // Function to update criteria-level conditional operator
  const updateApplicabilityConditionalOperator = (ruleId, operator) => {
    const updatedRule = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('applicabilityRules', updatedRule);
  };

  /* const removeApplicabilityCondition = (ruleId, condId) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: rule.conditions.filter(cond => cond.id !== condId)
            }
          : rule
      )
    );
  }; */

const removeApplicabilityCondition = (ruleId, condId) => {
    const updatedRules = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: rule.conditions.filter(cond => cond.id !== condId)
          }
        : rule
    );
    updateFormData('applicabilityRules', updatedRules);
  };


 /*  const addApplicabilityRule = () => {
    setApplicabilityRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      conditions: [] 
    }]);
  };
 */
  
  const addApplicabilityRule = () => {
    const updatedRules = [...applicabilityRules, { 
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: [],
      nextOperator: applicabilityRules.length > 0 ? "AND" : null
    }];
    updateFormData('applicabilityRules', updatedRules);
  };

  /* const updateApplicabilityRuleName = (ruleId, name) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, name } : rule
      )
    );
  }; */

const updateApplicabilityRuleName = (ruleId, name) => {
  const updatedRules = applicabilityRules.map(rule => 
    rule.id === ruleId ? { ...rule, name, isCustomName: true } : rule
  );
  updateFormData('applicabilityRules', updatedRules);
};


  /* const removeApplicabilityRule = (ruleId) => {
    setApplicabilityRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

  const removeApplicabilityRule = (ruleId) => {
    const updatedRules = applicabilityRules.filter(rule => rule.id !== ruleId);
    updateFormData('applicabilityRules', updatedRules);
  };

// UPDATED: Similar for redeem (use generateApplicabilityRuleName)
const updateRedeemCriteriaFromDate = (ruleId, fromDate) => {  // fromDate is YYYY-MM-DD
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updated = { ...rule, fromDate };
      // Regenerate name with effective dates
      //updated.name = updated.name || generateApplicabilityRuleName(updated);
      return updated;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};
const updateRedeemCriteriaToDate = (ruleId, toDate) => {  // toDate is YYYY-MM-DD
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updated = { ...rule, toDate };
      // Regenerate name with effective dates
      //updated.name = updated.name || generateApplicabilityRuleName(updated);
      return updated;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};

  const updateApplicabilityFrequency = (ruleId, frequency) => {
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updated = { ...rule, frequency: frequency ? Number(frequency) : null };
      // Regenerate name if changed
      //updated.name = generateApplicabilityRuleName(updated);
      return updated;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};

const updateApplicabilityPeriod = (ruleId, period) => {
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updated = { ...rule, period };
      // Regenerate name if changed
      //updated.name = generateApplicabilityRuleName(updated);
      return updated;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};


const updateApplicabilityScope = (ruleId, scope) => {
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updated = { ...rule, scope };
      // Regenerate name if changed
      //updated.name = generateApplicabilityRuleName(updated);
      return updated;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};

// Enhanced renderConditions function with conditional operators
  const renderConditions = (conditions, onUpdate, onRemove, parentId, onAdd) => (
    <div className="space-y-3">
      {conditions.map((cond, index) => {
        const selectedField = fields.find(f => f.value === cond.field);
        
        return (
          <div key={cond.id} className="space-y-3">
            {/* Conditional Operator Dropdown - Show for all conditions except the first */}
            {index > 0 && (
  <div className="flex justify-center">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
      <Select 
        value={conditions[index - 1]?.conditionalOperator || "AND"} 
        onValueChange={(value) => onUpdate(parentId, conditions[index - 1].id, "conditionalOperator", value)}
      >
        {conditionalOperators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            <div className="flex flex-col">
              <span className="font-medium">{op.label}</span>
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  </div>
)}

            
            {/* Condition Row */}
            <div className="flex gap-3 items-start p-2 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
                  <Select 
                    value={cond.field} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "field", value)}
                    searchable={true}
                    placeholder="Select Field"
                  >
                    {fields.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
                  <Select 
                    value={cond.operator} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "operator", value)}
                    searchable={true}
                    placeholder="Select Operator"
                  >
                    {operators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
                  {selectedField?.type === "multiselect" ? (
                    <MultiSelectDropdown
                      options={selectedField.options}
                      selectedValues={Array.isArray(cond.value) ? cond.value : (cond.value ? [cond.value] : [])}
                      onChange={(selectedValues) => onUpdate(parentId, cond.id, "value", selectedValues)}
                      placeholder={`Select ${selectedField.label}`}
                    />
                  ) : selectedField?.type === "select" ? (
                    <Select 
                      value={cond.value} 
                      onValueChange={(value) => onUpdate(parentId, cond.id, "value", value)}
                      searchable={true}
                      placeholder="Select Value"
                    >
                      {selectedField.options.map((option) => (
                        <SelectItem key={option.id} value={option.name}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={selectedField?.value === "amount" ? "number" : "text"}
                      value={cond.value}
                      onChange={(e) => onUpdate(parentId, cond.id, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  )}
                </div>
              </div>
              
              <div className="pt-6">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(parentId, cond.id)}
                  className="w-8 h-8 p-0 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}    
      <Button 
        variant="outline"
        onClick={() => onAdd(parentId)} 
        disabled={loading}
        className="w-full py-1 border-dashed border-2 hover:border-[#f7525a] hover:text-[#f7525a] transition-all duration-200"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {loading ? "Loading..." : "Add Condition"}
      </Button>
    </div>
  );
      


   return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-1">When can these coins be applied?</h3>
{/*             <p className="text-gray-600">Define the applicability conditions for using coins</p>
 */}          </div>
          <Button onClick={addApplicabilityRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Criteria
          </Button>
        </div>
        
        <div className="space-y-6">
          {applicabilityRules.map((rule, ruleIndex) => (
            <div key={rule.id}>
                          {ruleIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={applicabilityRules[ruleIndex-1]?.nextOperator || "AND"} 
                    onValueChange={(value) => updateApplicabilityConditionalOperator(applicabilityRules[ruleIndex - 1].id, value)}
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
                  {/* <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name /* || generateApplicabilityRuleName(rule) */}
                    onChange={(e) => updateApplicabilityRuleName(rule.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                     className="text-base"
                  />
                </div>
                {applicabilityRules.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeApplicabilityRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>

              {/* Date Range Section */}
              <div className="mb-6">
              {/*                 <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Date Range
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      From Date
                    </label>
                    <DateInput
                      value={rule.fromDate}
                      onChange={(date) => updateRedeemCriteriaFromDate(rule.id, date)}
                      placeholder="DD/MM/YYYY"
                      isToDate={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      To Date
                    </label>
                    <DateInput
                      value={rule.toDate}
                      onChange={(date) => updateRedeemCriteriaToDate(rule.id, date)}
                      placeholder="DD/MM/YYYY"
                      isToDate={true}
                    />
                  </div>
                </div>
              </div>


              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Applicability Settings
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Frequency (times)
                                  </label>
                    <Input
                      type="number"
                      placeholder="Unlimited (leave empty)"
                      value={rule.frequency || ''}
                      onChange={(e) => updateApplicabilityFrequency(rule.id, e.target.value)}
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">0 or empty = unlimited</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Period
                    </label>
                    <Select
                      value={rule.period || 'lifetime'}
                      onValueChange={(value) => updateApplicabilityPeriod(rule.id, value)}
                      placeholder="Select Period"
                    >
                      <SelectItem value="transaction">Per Transaction</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Scope
                    </label>
                    <Select
                      value={rule.scope || 'customer'}
                      onValueChange={(value) => updateApplicabilityScope(rule.id, value)}
                      placeholder="Select Scope"
                    >
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="global">Global</SelectItem>
                    </Select>
                  </div>
                </div>
              </div>

              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Conditions
                </label>
                {renderConditions(
                  rule.conditions,
                  updateApplicabilityCondition,
                  removeApplicabilityCondition,
                  rule.id,
                  addApplicabilityCondition
                )}
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
{/*           <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button> */}
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Redemption Rules Substep Component
const RedemptionRulesSubstep = ({ onNext,onCancel, onBack, formData, updateFormData, mode  }) => {
  /* const [redemptionRules, setRedemptionRules] = useState([
    { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage", 
      redeemValue: 0,
      applyTarget: "order"
    }
  ]); */
    const { sharedData } = useRewardRuleContext();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showRedeemModal, setShowRedeemModal] = useState(false);
    const [editedRedeemEntities, setEditedRedeemEntities] = useState([]);

    // Add useEffect to fetch data
  useEffect(() => {
    fetchCategories();
  }, []);

        // Fetch Categories
  const fetchCategories = async () => {
    const token = sessionStorage.getItem('token');
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedCategories = (data.data).map(cat => ({
        id:cat._id,
        name:cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

      // Use shared items from RewardCriteriaSubstep
  const availableItems = sharedData.fetchedItems.length > 0 
  ? sharedData.fetchedItems 
  : [{ id: "all", name: "All Items" }]; // Fallback

  // Use formData instead of local state
  const redemptionRules = formData.redemptionRules;

const generateRedemptionRuleName = (rule) => {
  if (!rule.redeemType || (!rule.redeemValue && rule.redeemValue !== 0)) {
    return "New Redemption Rule";
  }

  // Convert redeemValue to number if it's a string
  const redeemValue = typeof rule.redeemValue === 'string' ? parseFloat(rule.redeemValue) : rule.redeemValue;

  // Format redeem amount based on redeem type
  let redeemAmount = "";
  if (rule.redeemType === "fixed") {
    redeemAmount = `${redeemValue} coins`;
  } else if (rule.redeemType === "percentage") {
    redeemAmount = `${redeemValue}% coins`;
  }

  // Format what it's applied on
  let appliedOn = "";
  if (rule.applyTarget === "order") {
    appliedOn = "order value";
  } else if (rule.applyTarget === "item") {
    if (rule.applicableItems && rule.applicableItems.length > 0) {
      // Filter out "all" items if present
      const validItems = rule.applicableItems.filter(item =>
        item && (typeof item === 'object' ? item.id !== "all" : item !== "all")
      );
      
      if (validItems.length === 0) {
        appliedOn = "all items";
      } else if (validItems.length === 1) {
        const itemName = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        appliedOn = itemName || 'selected item';
      } else if (validItems.length === 2) {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'} and ${item2Name || 'item'}`;
      } else {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'}, ${item2Name || 'item'} and ${validItems.length - 2} more items`;
      }
    } else {
      appliedOn = "selected items";
    }
  }

  // Format redemption source
  let source = "";
  if (rule.source === "wallet") {
    source = "wallet";
  } else if (rule.source === "earned") {
    source = "earned coins";
  }

  // Format limit based on limit type
  let limitText = "";
  if (rule.limit && parseFloat(rule.limit) > 0) {
    const limitValue = parseFloat(rule.limit);
    
    if (rule.limitType === "fixed") {
      limitText = ` (max ${limitValue} coins per order)`;
    } else if (rule.limitType === "percentage") {
      if (rule.applyTarget === "order") {
        limitText = ` (max ${limitValue}% of order value)`;
      } else {
        limitText = ` (max ${limitValue}% of item value)`;
      }
    }
  }

  // Format minimum redemption requirement
  let minRedemptionText = "";
  if (rule.minRedemption && parseFloat(rule.minRedemption) > 0) {
    const minValue = parseFloat(rule.minRedemption);
    minRedemptionText = ` (min ${minValue} coins required)`;
  }

  // Format conversion rate if applicable
  let conversionText = "";
  if (rule.conversionRate && parseFloat(rule.conversionRate) > 0) {
    const rate = parseFloat(rule.conversionRate);
    conversionText = ` (${rate} coins = ₹1)`;
  }

  // Construct the final name
  let ruleName = "";
  
  if (rule.applyTarget === "order") {
    ruleName = `Redeem ${redeemAmount}  on ${appliedOn}${limitText}${minRedemptionText}${conversionText}`;
  } else if (rule.applyTarget === "item") {
    ruleName = `Redeem ${redeemAmount}  on ${appliedOn}${limitText}${minRedemptionText}${conversionText}`;
  } else {
    // Fallback for undefined applyTarget
    ruleName = `Redeem ${redeemAmount} ${limitText}${minRedemptionText}${conversionText}`;
  }

  return ruleName;
};



const updateRedemptionRuleName = (ruleId, name) => {
  const updatedRules = redemptionRules.map(rule => 
    rule.id === ruleId ? { ...rule, name, isCustomName: true } : rule
  );
  updateFormData('redemptionRules', updatedRules);
};

 /*  const updateRedemptionRule = (ruleId, key, value) => {
    setRedemptionRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, [key]: value };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateRedemptionRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

const updateRedemptionRule = (ruleId, key, value) => {
  const updatedRules = redemptionRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, [key]: value };
      // Always regenerate name when any field changes (except when explicitly setting the name)
      if (key !== 'name') {
        //updatedRule.name = generateRedemptionRuleName(updatedRule);
      }
      return updatedRule;
    }
    return rule;
  });
  updateFormData('redemptionRules', updatedRules);
};

  // Function to update redemption-level conditional operator
  const updateRedemptionRulesConditionalOperator = (ruleId, operator) => {
    const updatedRules = redemptionRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('redemptionRules', updatedRules);
  };

 /*  const addRedemptionRule = () => {
    setRedemptionRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage",
      redeemValue: 0,
      applyTarget: "order"
    }]);
  }; */

  /* const removeRedemptionRule = (ruleId) => {
    setRedemptionRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

const addRedemptionRule = () => {
    const updatedRules = [...redemptionRules, { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage",
      redeemValue: 0,
      applyTarget: "order",
      limit: 0,
      limitType: "fixed",
      nextOperator: redemptionRules.length > 0 ? "AND" : null // Add conditional operator for non-first criteria
    }];
    updateFormData('redemptionRules', updatedRules);
  };

  const removeRedemptionRule = (ruleId) => {
    const updatedRules = redemptionRules.filter(rule => rule.id !== ruleId);
    updateFormData('redemptionRules', updatedRules);
  };

// In RedemptionRulesSubstep, replace the updateRewardRuleApplicableItems function
const updateRedemptionRuleApplicableItems = (ruleId, selectedValues) => {
  // Get current rule to preserve user-edited values
  const currentRule = redemptionRules.find(r => r.id === ruleId);
  const existingItems = currentRule?.applicableItems || [];
  
  const selectedItems = selectedValues.map(itemId => {
    const item = availableItems.find(item => item.id === itemId);
    if (!item) return null;
    
    // FIXED: Preserve existing user-edited values for this item
    const existingEntity = existingItems.find(e => 
      (e.item_id === itemId || e.item_id === item.id || e.id === itemId)
    );
    
    return {
      item_id: item.item_id || item.id,
      entity_type: item.entity_type || '',
      item_name: item.item_name || item.name,
      item_image: item.item_image || '',
      // FIXED: Preserve user-edited redeem values if they exist
      is_redeemable: existingEntity?.is_redeemable !== undefined ? existingEntity.is_redeemable : (item.is_redeemable !== undefined ? item.is_redeemable : true),
      redeemable_value: existingEntity?.redeemable_value !== undefined && existingEntity?.redeemable_value !== null ? existingEntity.redeemable_value : (item.redeemable_value || 0),
      max_redeemable_value: existingEntity?.max_redeemable_value !== undefined && existingEntity?.max_redeemable_value !== null ? existingEntity.max_redeemable_value : (item.max_redeemable_value || 0),
      is_coins_distributable: existingEntity?.is_coins_distributable !== undefined ? existingEntity.is_coins_distributable : (item.is_coins_distributable !== undefined ? item.is_coins_distributable : false),
      max_quantity_redeemable: existingEntity?.max_quantity_redeemable !== undefined && existingEntity?.max_quantity_redeemable !== null ? existingEntity.max_quantity_redeemable : (item.max_quantity_redeemable || 0),
      // FIXED: Reward fields (will be cleaned by middleware for redeem type)
      is_rewardable: existingEntity?.is_rewardable !== undefined ? existingEntity.is_rewardable : (item.is_rewardable !== undefined ? item.is_rewardable : false),
      rewardable_value: existingEntity?.rewardable_value !== undefined && existingEntity?.rewardable_value !== null ? existingEntity.rewardable_value : (item.rewardable_value || 0),
      max_quantity_rewardable: existingEntity?.max_quantity_rewardable !== undefined && existingEntity?.max_quantity_rewardable !== null ? existingEntity.max_quantity_rewardable : (item.max_quantity_rewardable || 0),
    };
  }).filter(Boolean);
  const updatedRules = redemptionRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, applicableItems: selectedItems };
      //updatedRule.name = generateRedemptionRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('redemptionRules', updatedRules);
};

  return (
    <Card className="w-full">
      <CardContent className="p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-2">How many coins can be redeemed?</h3>
{/*             <p className="text-gray-600">Configure the redemption amounts and targets</p>
 */}          </div>
          <Button onClick={addRedemptionRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Redemption Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {redemptionRules.map((rule,ruleIndex) => (
            <div key={rule.id} >
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
                 {/*  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name /* || generateRedemptionRuleName(rule) */}
                    onChange={(e) => updateRedemptionRuleName(rule.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {redemptionRules.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRedemptionRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div>
{/*                 <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Redemption Configuration
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeem Type
                    </label>
                     <Select 
                      value={rule.redeemType} 
                      onValueChange={(value) => updateRedemptionRule(rule.id, "redeemType", value)}
                      placeholder="Select Redeem Type"
                    >
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeem Value
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter redeem value"
                      value={rule.redeemValue}
                      onChange={(e) => updateRedemptionRule(rule.id, "redeemValue", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeemed On
                    </label>
                    <Select 
                      value={rule.applyTarget} 
                      onValueChange={(value) => updateRedemptionRule(rule.id, "applyTarget", value)}
                      placeholder="Select Apply Target"
                    >
                      <SelectItem value="order">Order Value</SelectItem>
                      <SelectItem value="coins">Buildcoins</SelectItem>
                      <SelectItem value="item">Item Value</SelectItem>
                    </Select>
                  </div>
                  <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Redeemed From
                  </label>
                  <Select 
                    value={rule.source || 'wallet'}  // Default 'wallet'
                    onValueChange={(value) => updateRedemptionRule(rule.id, "source", value)}
                    placeholder="Select Source"
                  >
                    <SelectItem value="wallet">Wallet</SelectItem>
                    <SelectItem value="earned">Earned Coins</SelectItem>
                    <SelectItem value="immediate order">Immediate Order</SelectItem>
                    
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Redeemed Items
                  </label>
                  <div className="flex gap-2">  
                    <MultiSelectDropdown
                      options={availableItems}
                      selectedValues={rule.applicableItems ? 
                        rule.applicableItems.map(item => 
                          typeof item === 'object' ? item.item_id || item.id : item
                        ).filter(id => id !== "all") : []
                      }
                      onChange={(selectedValues) => updateRedemptionRuleApplicableItems(rule.id, selectedValues)}  // FIXED: Pass rule.id
                      placeholder="Select items"
                    />
                    {/* FIXED: Eye button to open modal */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditedRedeemEntities(rule.applicableItems || []);
                        setShowRedeemModal(true);
                      }}
                      className="w-8 h-8 p-0 flex-shrink-0"
                      title="View/Edit Entity Details"
                    >
                      <FontAwesomeIcon icon={faEye} className="text-gray-500 text-sm" />
                    </Button>
                  </div>
                </div>
                  <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="Max limit"
                        value={rule.limit}
                        onChange={(e) => updateRedemptionRule(rule.id, "limit", e.target.value)}
                      />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Limit Type</label>
                    <Select 
                        value={rule?.limitType}
                      onChange={(value) => updateRedemptionRule(rule.id, 'limitType', value)}
                        placeholder="Select Limit Type"
                      >
                        <SelectItem value="">Select Limit Type</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
        {showRedeemModal && (
        <EntityDetailsModal
          isOpen={showRedeemModal}
          onClose={() => setShowRedeemModal(false)}
          entities={editedRedeemEntities}
          type="redeem"
          onSave={(updatedEntities) => {
            setEditedRedeemEntities(updatedEntities);
            // FIXED: Update formData with edited entities (similar to reward)
            const updatedRules = redemptionRules.map(rule => {
              if (rule.id === rule.id) {  // FIXED: Use the current rule.id (pass via prop if needed for multi-rules)
                return { ...rule, applicableItems: updatedEntities };
              }
              return rule;
            });
            updateFormData('redemptionRules', updatedRules);
          }}
        />
      )}
      </CardContent>
    </Card>
  );
};

// UPDATED: Review and Create Substep Component
const ReviewAndCreateSubstep = ({ onCancel, onBack, onSubmit, formData, mode, type }) => {  // Add type prop
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} rule:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const isReward = type === 'reward';
  const criteriaSection = isReward ? formData.rewardCriteria : formData.applicabilityRules;
  const rulesSection = isReward ? formData.rewardRules : formData.redemptionRules;
  const criteriaLabel = isReward ? 'Reward Criteria' : 'Redeem Criteria';
  const rulesLabel = isReward ? 'Reward Rules' : 'Redemption Rules';
  return (
    <Card className="w-full">
      <CardContent className="p-8">
        <div className="mb-8">
          <h3 className="text-md font-bold text-gray-900 mb-2">{mode === 'edit' ? 'Review Your Changes' : `Review Your ${type.charAt(0).toUpperCase() + type.slice(1)} Rule`}</h3>
        </div>
        
        <div className="space-y-6">
          {/* Summary Cards - Type-aware: 2 cards for reward/redeem */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">{criteriaLabel}</p>
                  <p className="text-2xl font-bold text-blue-900">{criteriaSection.length}</p>
                </div>
                <div className="p-2 bg-blue-200 rounded-lg">
                  <FontAwesomeIcon icon={faRules} className="text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className={`p-4 bg-gradient-to-br rounded-xl border ${
              isReward 
                ? 'from-green-50 to-green-100 border-green-200' 
                : 'from-orange-50 to-orange-100 border-orange-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">{rulesLabel}</p>
                  <p className="text-2xl font-bold text-green-900">{rulesSection.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${
                  isReward ? 'bg-green-200' : 'bg-orange-200'
                }`}>
                  <FontAwesomeIcon icon={faCoins} className={`${isReward ? 'text-green-600' : 'text-orange-600'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Review Sections - Only relevant ones */}
          <div className="space-y-4">
            {/* Criteria Section */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faRules} className="mr-2 text-blue-600" />
                {criteriaLabel}
              </h4>
              <div className="space-y-3">
                {criteriaSection.map((criteria) => (
                  <div key={criteria.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{criteria.name || `${criteriaLabel} Rule`}</p>
                    {(criteria.fromDate || criteria.toDate) && (
                      <p className="text-sm text-gray-600 mt-1">
                        Valid from: {criteria.fromDate || formatDateForDisplay(new Date().toISOString())} to {criteria.toDate || formatDateForDisplay(new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString())}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {criteria.conditions.length} condition(s)
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Rules Section */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCoins} className={`mr-2 ${isReward ? 'text-green-600' : 'text-orange-600'}`} />
                {rulesLabel}
              </h4>
              <div className="space-y-3">
                {rulesSection.map((rule) => {
                  const isRewardRule = isReward ? rule : { ...rule, rewardType: rule.redeemType, rewardValue: rule.redeemValue, destination: 'N/A' };
                  return (
                    <div key={rule.id} className="p-4 bg-white rounded-lg border border-gray-100">
                      <p className="font-medium text-gray-900">{rule.name || `${rulesLabel} Rule`}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {isRewardRule.rewardType === "fixed" ? "Fixed" : "Percentage"} • 
                        Applied on {isRewardRule.applyTarget || 'order'} • 
                        {isReward ? `Destination: ${isRewardRule.destination}` : ''}
                      </p>
                      {rule.limit > 0 && (
                        <p className="text-sm text-gray-500 mt-1">Max limit: {rule.limit}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Hide applicability/redemption if not relevant; no expiry */}
          </div>
        </div>

        <div className="flex justify-between mt-4 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
            <Button variant="outline" onClick={onBack} size="sm" className="px-4">
              Back to Edit
            </Button>
            <Button 
              onClick={handleSubmit} 
              size="sm" 
              className="px-4 shadow-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2" />
                  {mode === 'edit' ? 'Updating Rule...' : 'Creating Rule...'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="mr-2" />
                  {mode === 'edit' ? 'Update Rule' : 'Create Rule'}
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// Main Reward Rule Builder Component
const RewardRulesEngine = ({ mode = 'create', existingRuleData = null, criteriaId = null, ruleId = null , type}) => {
  console.log("existingRuleData",existingRuleData)
  //const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [isSaving,setIsSaving] = useState(false)
  const navigate = useNavigate();
console.log("type",type)
useEffect(() => {
  if (mode === 'edit' || mode === 'create') {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // 2 seconds

    return () => clearTimeout(timer); // clean up
  }
}, [mode]);

  
  // Helper function to transform backend data to frontend format
// Replace the existing transformBackendDataToFrontend
const transformBackendDataToFrontend = (backendData, docType) => {
  if (!backendData) return getDefaultFormData(docType);

    // Helper to convert backend date (ISO/null) to display DD/MM/YYYY with defaults
  const convertBackendDateToDisplayWithDefault = (backendDate, isToDate = false) => {
    if (!backendDate || backendDate === null) {
      const today = new Date();
      if (isToDate) {
        today.setFullYear(today.getFullYear() + 1);
      }
      return formatDateForDisplay(today.toISOString());  // DD/MM/YYYY
    }
    // Existing conversion for valid dates
    return formatDateForDisplay(backendDate);
  };

  const criteria = backendData.criteria?.map(c => {
    const fromDisplay = convertBackendDateToDisplayWithDefault(c.fromDate, false);  // Global helper: Today if null
    const toDisplay = convertBackendDateToDisplayWithDefault(c.toDate, true);
    return {
      id: c._id || uuidv4(),
      name: c.name || "",
      fromDate: fromDisplay,  // Now non-empty display format
      toDate: toDisplay,
      conditions: (c.conditions || []).map(cond => ({
        id: cond._id || uuidv4(),
        field: cond.field,
        operator: cond.operator,
        value: cond.value,
        conditionalOperator: cond.conditionalOperator || 'AND'
      })),
      nextOperator: c.nextOperator || 'AND',
      frequency: c.frequency || null,
      period: c.period || 'lifetime',
      scope: c.scope || 'customer'
    };
  }) || [];

  const backendRules = backendData.rules?.map(r => ({
    id: r._id || uuidv4(),
    name: r.name || "",
    type: r.type,  // 'fixed' or 'percentage'
    value: r.value || 0,
    on: r.on || 'order',
    limit: r.limit || 0,
    limitType: r.limitType || 'fixed',
    limitOn: r.limitOn || 'order',
    to: r.to,
    from: r.from,
    applicableItems: (r.applicableEntities || []).map(e => ({
              item_id: e.item_id,  // string from backend
        entity_type: e.entity_type || '',
        item_name: e.item_name,
        item_image: e.item_image || '',
        // FIXED: Preserve ALL reward/redeem fields from backend (for modal mapping in edit mode)
        is_rewardable: e.is_rewardable !== undefined ? e.is_rewardable : true,  // Boolean or string from backend
        rewardable_value: e.rewardable_value !== undefined ? Number(e.rewardable_value) : 10,  // NEW: Number, default 10 if missing
        max_quantity_rewardable: e.max_quantity_rewardable !== undefined ? Number(e.max_quantity_rewardable) : 5,  // NEW: Default 5
        is_redeemable: e.is_redeemable !== undefined ? e.is_redeemable : false,
        redeemable_value: e.redeemable_value !== undefined ? Number(e.redeemable_value) : 10,  // NEW: Default 10 if missing
        max_redeemable_value: e.max_redeemable_value !== undefined ? Number(e.max_redeemable_value) : 0,  // NEW: Default 0 if missing
        is_coins_distributable: e.is_coins_distributable !== undefined ? e.is_coins_distributable : false,
        max_quantity_redeemable: e.max_quantity_redeemable !== undefined ? Number(e.max_quantity_redeemable) : 5,  // NEW: Default 5
        // Fallback id/name for compatibility
        id: e.item_id,
        name: e.item_name
    }))
  })) || [];

  let transformed = getDefaultFormData(docType);

  if (docType === 'reward') {
    transformed.rewardCriteria = criteria;
    transformed.rewardRules = backendRules.map(rule => ({
      ...rule,
      rewardType: rule.type,
      rewardValue: rule.value,
      applyTarget: rule.on,
      destination: rule.to,
      limitType: rule.limitType
      // applicableItems already mapped
    }));
  } else if (docType === 'redeem') {
    transformed.applicabilityRules = criteria;
    transformed.redemptionRules = backendRules.map(rule => ({
      ...rule,
      redeemType: rule.type,
      redeemValue: rule.value,
      applyTarget: rule.on || rule.limitOn || 'order',
      limitType: rule.limitType
      // from: rule.from (add UI later if needed)
      // applicableItems already mapped
    }));
  }

  return transformed;
};

// UPDATED: Enhanced to use defaults (can replace the simple version)
const convertBackendDateToDisplay = (backendDate, isToDate = false) => {
  return convertBackendDateToDisplayWithDefault(backendDate, isToDate);  // Now global, no undef error
};

  // Default form data functions
const getDefaultFormData = (docType = 'reward') => ({
  rewardCriteria: docType === 'reward' ? [getDefaultRewardCriteria()] : [],
  rewardRules: docType === 'reward' ? [getDefaultRewardRule()] : [],
  applicabilityRules: docType === 'redeem' ? [getDefaultApplicabilityRule()] : [],
  redemptionRules: docType === 'redeem' ? [getDefaultRedemptionRule()] : [],
});

// UPDATED: Pre-populate fromDate with today's DD/MM/YYYY
const getDefaultRewardCriteria = () => {
  const today = new Date();
  const todayDisplay = formatDateForDisplay(today.toISOString());  // e.g., "11/10/2024"
  
  const oneYearAhead = new Date(today);
  oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1);
  const oneYearDisplay = formatDateForDisplay(oneYearAhead.toISOString());  // e.g., "11/10/2025"
  return {
    id: uuidv4(),
    name: "",
    fromDate: todayDisplay,  // Display format; converts to ISO on save
    toDate: oneYearDisplay,  // Empty; defaults to 1 year in payload
    conditions: [],
    nextOperator: "AND",
    frequency: null,
    period: 'lifetime',  // Or 'transaction' to match example
    scope: 'customer'
  };
};

  const getDefaultRewardRule = () => ({
  id: uuidv4(),
  name: "",
  rewardType: "fixed",
  rewardValue: 0,
  applyTarget: "order",
  destination: "wallet",
  limit: 0,
  limitType: "fixed",
  applicableItems: [],
});

// UPDATED: Same for redeem criteria
const getDefaultApplicabilityRule = () => {
  const today = new Date();
  const todayDisplay = formatDateForDisplay(today.toISOString());
  
  const oneYearAhead = new Date(today);
  oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1);
  const oneYearDisplay = formatDateForDisplay(oneYearAhead.toISOString());
  return {
    id: uuidv4(),
    name: "",
    fromDate: todayDisplay,
    toDate: oneYearDisplay,  // Empty; defaults in payload
    conditions: [],
    nextOperator: "AND",
    frequency: null,
    period: 'lifetime',  // Or 'transaction'
    scope: 'customer'
  };
};

  const getDefaultRedemptionRule = () => ({
  id: uuidv4(),
  name: "",
  redeemType: "percentage",
  redeemValue: 0,
  applyTarget: "order",
  limit: 0,
  limitType: "fixed",
  applicableItems: [],
});


// In RewardRulesEngine component, replace the useState for formData
const [formData, setFormData] = useState(() => {
  if (mode === 'edit' && existingRuleData) {
    return transformBackendDataToFrontend(existingRuleData, type);
  }
  return getDefaultFormData(type);
});
// Also update the useEffect for existingRuleData changes
useEffect(() => {
  if (mode === 'edit' && existingRuleData) {
    setFormData(transformBackendDataToFrontend(existingRuleData, type));
  }
}, [mode, existingRuleData, type]);  // Add type dependency


  // Update form data function
  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };


// Dynamically build steps based on type
const allSteps = {
  reward: [
    {
      id: 'criteria',
      title: 'Reward Criteria',
      description: 'When to give coins',
      component: RewardCriteriaSubstep
    },
    {
      id: 'rewards',
      title: 'Rewards',
      description: 'How many coins',
      component: RewardRulesSubstep
    },
    {
      id: 'review',
      title: 'Review',
      description: 'Review & create',
      component: ReviewAndCreateSubstep
    }
  ],
  redeem: [
    {
      id: 'applicability',
      title: 'Redeem Criteria',
      description: 'Where to use coins',
      component: ApplicabilityRulesSubstep
    },
    {
      id: 'redemption',
      title: 'Redemption',
      description: 'How to redeem',
      component: RedemptionRulesSubstep
    },
    {
      id: 'review',
      title: 'Review',
      description: 'Review & create',
      component: ReviewAndCreateSubstep
    }
  ]
};

// Pick steps based on `type`
const steps = allSteps[type] || allSteps.reward;


  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

const handleStayHere = () => {
  setShowCancelPopup(false);
}

const handleCancel = () => {
  setShowCancelPopup(true);
}

const handleLeave = () => {
  navigate(-1);
}


  // Start at review step when editing, else start from first step
  //const [currentStep, setCurrentStep] = useState(() => (mode === 'edit' ? steps.length - 1 : 0));

  const [currentStep, setCurrentStep] = useState(0);

// Reset step when type changes
useEffect(() => {
  setCurrentStep(0);
}, [type]);




     // Simple date conversion function
  const convertDateFormat = (dateString) => {
    if (!dateString) return null;
    
    // If it's DD/MM/YYYY, convert to YYYY-MM-DD
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    
    // If it's already YYYY-MM-DD, return as is
    return dateString;
  };

// Handle submit for both create and update
const handleSubmit = async () => {
  try {
    let payload = {
      type,
      criteria: [],
      rules: []
    };

    // Hardcode schema defaults for criteria (add UI later if needed)
    const criteriaDefaults = {
      frequency: 1,  // As in your example
      period: 'transaction',
      scope: 'customer'
    };

    if (type === 'reward') {
      payload.criteria = formData.rewardCriteria.map(criteria => ({
        name: criteria.name || 'Default Criteria',
        fromDate: convertToISODate(criteria.fromDate, false),  // Today if null
        toDate: convertToISODate(criteria.toDate, true),     
        conditions: criteria.conditions.map(cond => ({
          field: cond.field,
          operator: cond.operator,
          value: cond.value,
          conditionalOperator: cond.conditionalOperator || 'AND'
        })),
        nextOperator: criteria.nextOperator || 'AND',
        frequency: Number(criteria.frequency) || null,  // Ensure number or null
        period: criteria.period || 'lifetime',
        scope: criteria.scope || 'customer',
        ...criteriaDefaults
      }));

      payload.rules = formData.rewardRules.map(rule => ({
        name: rule.name || 'Default Rule',
        type: rule.rewardType,
        value: Number(rule.rewardValue) || 0,
        on: rule.applyTarget || 'order',
        limit: Number(rule.limit) || 0,
        limitType: rule.limitType || 'fixed',
        limitOn: rule.applyTarget || 'order',  // Default to on
        to: rule.destination || 'wallet',  // For reward
        applicableEntities: rule.applicableItems
          ?.filter(entity => entity.item_id && entity.item_name)  // FIXED: Filter valid entities only
          ?.map(entity => ({
            item_id: entity.item_id,  // Send as string; backend should convert to ObjectId if needed
            entity_type: entity.entity_type || '',
            item_name: entity.item_name,
            item_image: entity.item_image || '',
            // FIXED: Preserve reward-specific fields (redeem ones will be cleaned by middleware)
            is_rewardable: entity.is_rewardable !== undefined ? entity.is_rewardable : true,
            rewardable_value: entity.rewardable_value || 0,
            max_quantity_rewardable: entity.max_quantity_rewardable || 0,
            // FIXED: Include but let middleware clean redeem fields for reward type
            is_redeemable: entity.is_redeemable || false,
            redeemable_value: entity.redeemable_value || 0,
            max_redeemable_value: entity.max_redeemable_value || 0,
            is_coins_distributable: entity.is_coins_distributable || false,
            max_quantity_redeemable: entity.max_quantity_redeemable || 0,
          })) || [],
      }));
    } else if (type === 'redeem') {
      payload.criteria = formData.applicabilityRules.map(criteria => ({
        name: criteria.name || 'Default Criteria',
        fromDate: convertToISODate(criteria.fromDate, false),  // Today if null
        toDate: convertToISODate(criteria.toDate, true),
        conditions: criteria.conditions.map(cond => ({
          field: cond.field,
          operator: cond.operator,
          value: cond.value,
          conditionalOperator: cond.conditionalOperator || 'AND'
        })),
        nextOperator: criteria.nextOperator || 'AND',
        frequency: Number(criteria.frequency) || null,  // Ensure number or null
        period: criteria.period || 'lifetime',
        scope: criteria.scope || 'customer',
        ...criteriaDefaults
      }));

      payload.rules = formData.redemptionRules.map(rule => ({
        name: rule.name || 'Default Rule',
        type: rule.redeemType,
        value: Number(rule.redeemValue) || 0,
        on: rule.applyTarget || 'order',
        limit: Number(rule.limit) || 0,
        limitType: rule.limitType || 'fixed',
        limitOn: rule.applyTarget || 'order',
        from: 'wallet',  // Hardcode (add UI Select like destination if needed)
        applicableEntities: rule.applicableItems
          ?.filter(entity => entity.item_id && entity.item_name)  // FIXED: Filter valid entities only
          ?.map(entity => ({
            item_id: entity.item_id,  // Send as string; backend should convert to ObjectId if needed
            entity_type: entity.entity_type || '',
            item_name: entity.item_name,
            item_image: entity.item_image || '',
            // FIXED: Preserve redeem-specific fields (reward ones will be cleaned by middleware)
            is_redeemable: entity.is_redeemable !== undefined ? entity.is_redeemable : true,
            redeemable_value: entity.redeemable_value || 0,
            max_redeemable_value: entity.max_redeemable_value || 0,
            is_coins_distributable: entity.is_coins_distributable || false,
            max_quantity_redeemable: entity.max_quantity_redeemable || 0,
            // FIXED: Include but let middleware clean reward fields for redeem type
            is_rewardable: entity.is_rewardable || false,
            rewardable_value: entity.rewardable_value || 0,
            max_quantity_rewardable: entity.max_quantity_rewardable || 0,
          })) || [],
      }));
    }

    // FIXED: Validate ruleId for edit mode
    if (mode === 'edit' && (!ruleId || ruleId.trim() === '')) {
      throw new Error('Missing ruleId for update operation. Please provide the existing rule _id.');
    }

    setIsSaving(true);
    console.log('Payload for backend:', payload);
    console.log('Mode:', mode, 'RuleId:', ruleId);  // Debug: Confirm ruleId is present

    // FIXED: Use path param for rulesId in edit mode (e.g., /update-rules/68e9ef20b402451185664c6f)
    let url;
    if (mode === 'edit') {
      url = `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/update-rules?rulesId=${ruleId}`;
    } else {
      url = `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/create-rules`;
    }
    console.log('Request URL:', url);  // Debug: Log full URL

    const response = await fetch(url, {
      method: mode === 'edit' ? 'PUT' : 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'x-token': sessionStorage.getItem('token')  // Auth token
      },
      body: JSON.stringify(payload)  // No rulesId in body
    });

    // FIXED: Enhanced error handling with full response details
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
        console.error('Backend error response:', errorData);  // Debug: Full error details
      } catch (parseError) {
        // If not JSON, read as text
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
        console.error('Raw error response:', errorText);  // Debug: Raw text if not JSON
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Success response:', result);  // Debug: Success details
    setIsSaving(false);
    alert(`Rules ${mode === 'edit' ? 'updated' : 'created'} successfully!`);

    setIsLoading(true);
    setTimeout(() => {
      window.location.href = '/bb/app/rewardrules/rewardruleslist';
      setIsLoading(false);
    }, 1000);

  } catch (error) {
    console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} rules:`, error);
    alert(`Error ${mode === 'edit' ? 'updating' : 'creating'} rules: ${error.message}`);
    setIsSaving(false);
  }
};


  if (isLoading) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading reward rules...</p>
        </div>
      </div>
    );
  }

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <RewardRuleProvider>
      <div className="h-full flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Fixed Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto p-4">
            {/* Title Section */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {mode === 'edit' ? `Edit ${type.charAt(0).toUpperCase() + type.slice(1)} Rule` : `Create New ${type.charAt(0).toUpperCase() + type.slice(1)} Rule`}
                </h1>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between overflow-x-auto">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center cursor-pointer hover:opacity-70" onClick={() => setCurrentStep(index)}>
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200 ${
                        index < currentStep 
                          ? 'bg-green-500 text-white' 
                          : index === currentStep 
                          ? 'bg-[#f7525a] text-white' 
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {index < currentStep ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="mt-2 text-center">
                        <p className={`text-sm font-medium ${
                          index <= currentStep ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-16 h-0.5 mx-4 transition-all duration-200 ${
                        index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4">
            <CurrentStepComponent 
              onNext={handleNext}
              onBack={handleBack}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              formData={formData}
              updateFormData={updateFormData}
              mode={mode}
              type={type}
            />
          </div>
        </div>
      </div>

      {/* Cancel Popup */}
      {showCancelPopup && (
        <div className="fixed inset-0 flex items-top justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-b-md shadow-md w-[550px] h-[250px] flex flex-col" style={{ maxWidth: '550px', maxHeight: '250px' }}>
            <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-14 px-6'>
              <h2 className="text-md font-semibold">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-yellow-500 mr-4 " />
                Leave this page?
              </h2>
              <button onClick={handleStayHere} className="text-white px-1 py-1 rounded-md ">   
                <FontAwesomeIcon icon={faX} className="text-xs text-[#212529] cursor-pointer " />
              </button>
            </div>
            <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
            <div className='pt-4 pl-4 '>
              <div className="w-full items-center p-5">
                <label htmlFor="Other_details" className="block text-sm font-regular text-[#212529] ">
                  If you leave, your unsaved changes will be discarded.
                </label>
              </div>  
            </div>
            <div className="mt-auto w-full">
              <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
              <div className="text-sm flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
                <button className="px-2 py-1 bg-[#408dfb] border-[#f7525a] text-white rounded-md" onClick={handleStayHere}>
                  Stay Here  {isSaving && (
                    <Spinner/>
                  )}
                </button>
                <button onClick={handleLeave} 
                  className="hover:border-[#DDDDDD] focus:border-[#DDDDDD] focus:ring focus:ring-[#DDDDDD] focus:ring-opacity-50  px-2 py-1 bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD] rounded-md">
                  Leave & Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </RewardRuleProvider>
  );
};


export default RewardRulesEngine;
